import { inject, Injectable } from '@angular/core';
import { Logger, LoggerFactory } from '../core/logger.service';
import { BehaviorSubject, EMPTY, forkJoin, Observable, of, Subject } from 'rxjs';
import { WidgetService } from '../shared/services/widget.service';
import {
  ActionComponent,
  ActionOnComponent,
  ButtonActionType,
  ButtonComponent,
  ButtonListComponent,
  ComponentListItem,
  CustomProfileProperty,
  EditedComponentPart,
  EditedComponentSidebarSection,
  EditingDevice,
  EditModeComponent,
  FormSettings,
  FormSettingsHandlerTypes,
  FourSideStylingEntityWrapper,
  ProductTourProperties,
  SidebarState,
  StepNavigationLastButtonType,
  TourAlignment,
  TourConnectorSize,
  TourConnectorStyle,
  TourPosition,
  Widget,
  WidgetComponent,
  WidgetComponentPatchRequest,
  WidgetComponentPatchRequestItem,
  WidgetComponentSubType,
  WidgetComponentType,
  WidgetContainer,
  WidgetDeviceView,
  WidgetFontDescription,
  WidgetLayout,
  WidgetPosition,
  WidgetSimpleInfo,
  WidgetState,
  WidgetUpdateProgress,
} from './widgets.model';
import { catchError, delay, map, share, switchMap, tap } from 'rxjs/operators';
import { CampaignService } from '../shared/services/campaign.service';
import { OnsiteCampaign } from './campaign.model';
import WidgetComponentHelpers from './widget-component-helpers';
import { SnackBarPosition, SnackBarService } from '../core/snack-bar.service';
import { AccountSettingsService } from '../account-settings/account-settings.service';
import { AccountCustomFont, AccountVariable } from '../account-settings/account-settings-models';
import { Account } from '../back-office/account.model';
import { LanguageService } from '../core/language.service';
import { AccountLanguageService } from '../core/account-language.service';
import { AutomationsService } from '../automations/automations.service';
import { CreateAutomationService } from '../automations/create-automation/create-automation.service';
import { IntegrationType } from 'app/automations/integrations-models';
import { IntegrationsService } from 'app/shared/services/integrations.service';
import { SessionConditionType } from 'app/audience/components/session-condition-group.model';
import { AudienceFilterOperator } from 'app/audience/components/audience.model';
import { WidgetAutomationsDataService } from './widget-automations-data.service';
import { AutomationAction } from 'app/automations/automations-models';
import WidgetAutomationHelpers from './widget-automation-helpers';
import {
  ActionRealTimeRegistrationType,
  ActionRealTimeScl,
  ACTIONS_TYPES,
  ACTIONS_TYPES_REAL_TIME,
  ActionWithRealTime,
  REAL_TIME_HANDLER_TYPE,
} from 'app/automations/automation-helpers-actions';
import { WidgetAutomationsService } from './widget-automations.service';
import { CurrentContextService } from 'app/core/current-context.service';
import { FlyoutService } from 'app/flyout/flyout.service';
import { EarlyAccessFeatures } from 'app/core/identity/user-identity';
import { SubscriptionHandlerBaseComponent } from 'app/core/subscription-handler.base';
import { ConfirmationDialogComponent } from 'app/shared/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { WidgetStatesService } from './widget-states.service';
import { WidgetComponentsService } from './widget-components.service';
import { DataRepeaterService } from './widget-editor/data-repeater.service';
import { WidgetPreviewService } from './campaigns/widget-preview.service';
import { mergeUnsavedEdits } from './widget-editor/merge-unsaved-edits';

// What the sidebar can have open: a component, a step or a container.
type EditableItem = WidgetComponent | WidgetState | WidgetContainer;

// An empty setting a panel fills in on its own, such as a mobile setting with nothing in it, is not an edit.
function withoutEmptyValues(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(withoutEmptyValues);
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value)
      .map(([key, item]) => [key, withoutEmptyValues(item)])
      .filter(([, item]) => item !== null && item !== undefined);
    return entries.length > 0 ? Object.fromEntries(entries) : undefined;
  }
  return value;
}

@Injectable()
export class WidgetEditorDataService extends SubscriptionHandlerBaseComponent {
  private readonly logger: Logger;
  public customFontsBS = new BehaviorSubject<Array<AccountCustomFont>>(undefined);
  public accountVariablesBS = new BehaviorSubject<Array<AccountVariable>>(undefined);
  public simpleWidgetsBS = new BehaviorSubject<Array<WidgetSimpleInfo>>(undefined);
  public allTagsBS = new BehaviorSubject<Array<string>>(undefined);
  public customProfilePropertiesBS = new BehaviorSubject(undefined);
  public campaignBS = new BehaviorSubject<OnsiteCampaign>(undefined);
  public currentEditorWidgetBS = new BehaviorSubject<number>(undefined);
  public openSelectedStateBS = new BehaviorSubject(undefined);
  public widgetBS = new BehaviorSubject<Widget>(undefined);
  public selectedIntegration?: { key?: IntegrationType; translation?: string; iconPath?: string };
  public widgetTransitionBS = new BehaviorSubject<{
    widget: Widget;
    type: string;
  }>(undefined);
  public playContainerEffectBS = new BehaviorSubject<{
    component: WidgetComponent;
    playAnimation: boolean;
  }>(undefined);
  public previewDropdownBS = new BehaviorSubject<{
    id: number;
    preview: boolean;
  }>(undefined);
  public widgetStateBS = new BehaviorSubject<WidgetState>(undefined);
  public currentlyEditedComponentBS = new BehaviorSubject<EditModeComponent>({} as EditModeComponent);
  public currentlyEditedItemListBS = new BehaviorSubject<ComponentListItem>(null);
  public actionOnComponentBS = new BehaviorSubject<ActionComponent>(undefined);
  public savedStateBS = new BehaviorSubject<WidgetState>(undefined);
  public refreshComponentSubject = new BehaviorSubject<WidgetComponent>(null);
  public currentStateId: number;
  public currentDeviceViewBS = new BehaviorSubject<WidgetDeviceView>(WidgetDeviceView.Desktop);
  public currentEditingDeviceBS = new BehaviorSubject<EditingDevice>(EditingDevice.Mobile);
  public updateInProgressBS = new BehaviorSubject<any>(undefined);
  public hoveredComponentBS = new BehaviorSubject<WidgetComponent>(undefined);
  public isCurrentTabChangedBS = new BehaviorSubject<boolean>(false);

  public visibleContainerIdsBS = new BehaviorSubject<Array<number>>(undefined);
  public visibleComponentIdsBS = new BehaviorSubject<Array<number>>(undefined);

  public customAndInitialHeightImg = new BehaviorSubject<any[]>([undefined, undefined]);
  public currentCampaignTypeBS = new BehaviorSubject<string>(undefined);

  public showLeftSidebarBS = new BehaviorSubject<{ sidebarState: SidebarState; shouldAnimateCopy: boolean }>(undefined);
  public showPrimaryCanvasLeftSidebarSubject = new Subject<WidgetState>();
  public showRightSidebarBS = new BehaviorSubject<{ sidebarState: SidebarState }>(undefined);

  public isTemplateCampaign = false;
  private isSaveable = true;
  public isChanges: boolean;
  public originalComponent: any;
  public hoveringCanvasBS = new BehaviorSubject<boolean>(false);
  public confirmSocialSecurityNumberResult$ = new Subject<boolean | null>();
  public dataRepetearShowCopiesBS = new BehaviorSubject<boolean>(true);
  public clipboardComponentsBS = new BehaviorSubject<WidgetComponent[]>(this.getClipboardComponents());
  private lastButtonMarkedForPersist: WidgetComponent | undefined;
  // Last source metadata sent per automation id, so an unchanged label is not re-sent on every save.
  private readonly sentAutomationSourceByAutomationId = new Map<string, string>();
  private readonly beingRemoved = new Set<EditableItem>();

  private widgetPreviewService = inject(WidgetPreviewService);

  constructor(
    loggerFactory: LoggerFactory,
    private widgetService: WidgetService,
    private campaignService: CampaignService,
    private accountSettingsService: AccountSettingsService,
    private readonly automationService: AutomationsService,
    private readonly createAutomationService: CreateAutomationService,
    private languageService: LanguageService,
    public accountLanguageService: AccountLanguageService,
    private snackbarService: SnackBarService,
    private readonly integrationsService: IntegrationsService,
    private readonly widgetAutomationsDataService: WidgetAutomationsDataService,
    private widgetAutomationsService: WidgetAutomationsService,
    private currentContextService: CurrentContextService,
    private flyoutService: FlyoutService,
    private dialog: MatDialog,
  ) {
    super();
    this.logger = loggerFactory.get('WidgetEditorDataService');
  }

  public get defaultDeviceView(): WidgetDeviceView {
    const pref = this.currentContextService.currentContext?.customer?.defaultEditorView;
    return pref === WidgetDeviceView.Mobile ? WidgetDeviceView.Mobile : WidgetDeviceView.Desktop;
  }

  /* Temporary code to populate subType based on type until the back-end is fixed for this, otherwise everything will break */
  private getDefaultSubtypeFromComponentType(componentType: WidgetComponentType) {
    if (componentType) {
      switch (componentType) {
        case WidgetComponentType.Text:
          return WidgetComponentSubType.TextRegular;
        case WidgetComponentType.Button:
          return WidgetComponentSubType.ButtonRegular;
        case WidgetComponentType.Image:
          return WidgetComponentSubType.ImageRegular;
        case WidgetComponentType.Spacer:
          return WidgetComponentSubType.SpacerRegular;
        case WidgetComponentType.Dropdown:
          return WidgetComponentSubType.DropdownRegular;
        default:
          return undefined;
      }
    }
    return undefined;
  }

  public setIsSaveable(isSaveable: boolean): void {
    this.isSaveable = isSaveable;
  }

  public checkIfSaveable(): boolean {
    return this.isSaveable && this.currentlyEditedComponent?.component != null;
  }

  public loadWidgetById(widgetId: number, isStaticWidget: boolean) {
    if (widgetId) {
      // Clear out objects for new widget
      if (this.widget) {
        this.clearOutWidgetData();
      } else {
        // First load: context is now available, apply customer preference
        this.currentDeviceViewBS.next(this.defaultDeviceView);
      }

      this.fetchWidget(widgetId, isStaticWidget).subscribe(
        (widget) => this.showLoadedWidget(widget),
        (error) => {
          this.logger.error('Error on widget retrieval from server: ' + error);
        },
      );
    }
  }

  // Reloads the open widget after it changed outside the editor, keeping the open step and device. Unsaved edits on
  // the open component are re-applied to the reloaded copy while the old step is still shown, because the sidebar
  // reads the step, and setting the step again saves the open component. `conflict` is true when both changed the
  // same value.
  public reloadWidget(widgetId: number, isStaticWidget: boolean): Observable<{ widget: Widget; conflict: boolean }> {
    const edited = this.currentlyEditedComponent;
    return this.fetchWidget(widgetId, isStaticWidget).pipe(
      map((widget) => {
        const conflict = this.reapplyUnsavedEdits(edited, widget);
        // Canvas components render their content once, so the step is emptied for a tick to rebuild the canvas.
        this.widgetStateBS.next(undefined);
        return { widget, conflict };
      }),
      delay(0),
      tap(({ widget }) => this.showLoadedWidget(widget)),
      catchError((error) => {
        this.logger.error('Error on widget retrieval from server: ' + error);
        return EMPTY;
      }),
    );
  }

  private fetchWidget(widgetId: number, isStaticWidget: boolean): Observable<Widget> {
    const getWidget = isStaticWidget
      ? this.widgetService.getStaticWidgetById(widgetId)
      : this.widgetService.getWidgetById(widgetId);
    return getWidget.pipe(
      tap((widget: Widget) => {
        if (widget) {
          widget.states.forEach((state) => {
            this.prepareState(state);
          });
        }
      }),
    );
  }

  private showLoadedWidget(widget: Widget): void {
    this.widgetBS.next(widget);
    this.widgetService.hasUnpublishedChangesBS.next(widget.hasUnpublishedChanges);
    this.logger.debug('Loaded widget: ' + JSON.stringify(widget));
    if (this.currentStateId && this.widget.states) {
      const foundState = this.widget.states.find((state) => state.id === this.currentStateId);
      if (foundState) {
        this.setCurrentWidgetState(foundState);
      } else {
        this.setCurrentWidgetState(this.widget.states[0]);
      }
    } else if (this.widget.states) {
      const splittedStates = WidgetComponentHelpers.splitWidgetStatesByType(this.widget.states);
      if (splittedStates.regulars && splittedStates.regulars.length > 0) {
        this.setCurrentWidgetState(splittedStates.regulars[0]);
      } else if (splittedStates.success) {
        this.setCurrentWidgetState(splittedStates.success);
      } else if (splittedStates.teaser) {
        this.setCurrentWidgetState(splittedStates.teaser);
      } else {
        throw new Error('No states found after splitting them!');
      }
    } else {
      this.logger.error('Widget did not have any states');
    }
  }

  public prepareState(state: WidgetState) {
    const floatingContainerIndex = state.containers.findIndex(
      (cont) => cont.type === WidgetComponentType.ContainerFloating,
    );
    if (floatingContainerIndex !== -1) {
      state.floatingContainer = JSON.parse(JSON.stringify(state.containers[floatingContainerIndex]));
      state.floatingContainer.type = WidgetComponentType.ContainerFloating;
      state.floatingContainer.components.forEach((component) => {
        component.containerId = state.floatingContainer.id;
        if (component.type === WidgetComponentType.Image) {
          component.previewUrl = this.replaceAccountVariblesWithValues(component.url);
        }
      });
      state.containers.splice(floatingContainerIndex, 1);
    }
    state.containers.forEach((container) => {
      container.type = WidgetComponentType.ContainerRegular;
      container.subType = WidgetComponentSubType.ContainerRegular;

      container.components.sort((a, b) => a.order - b.order);
      container.components.forEach((component) => {
        if (!component.subType) {
          component.subType = this.getDefaultSubtypeFromComponentType(component.type);
        }
        component.containerId = container.id;
        // If component has children, set same container even on children
        WidgetComponentsService.setChildrenProperties(component, container.id);
        if (component.type === WidgetComponentType.Image) {
          component.previewUrl = this.replaceAccountVariblesWithValues(component.url);
        }
      });
    });
  }

  public replaceAccountVariblesWithValues(original: string): string {
    if (original) {
      this.accountVariables.forEach((x) => {
        original = original.replace(`{${x.key}}`, x.value);
      });
    }
    return original;
  }

  public loadAccountVariables(): Observable<Array<AccountVariable>> {
    this.accountVariablesBS.next([]);

    const observable = this.accountSettingsService.getAllAccountVariables().pipe(share());

    observable.subscribe(
      (variables: Array<AccountVariable>) => {
        this.accountVariablesBS.next(variables);
        this.logger.debug('Loaded account variables: ' + JSON.stringify(this.accountVariables));
      },
      (error) => {
        this.logger.error('Error on account variables retrieval from server: ' + error);
      },
    );

    return observable;
  }

  public loadAllSimpleWidgets(): Observable<Array<WidgetSimpleInfo>> {
    this.simpleWidgetsBS.next([]);

    const observable = this.widgetService.getAllWidgetsSimpleInfo().pipe(share());

    observable.subscribe(
      (result) => {
        this.simpleWidgetsBS.next(result);
        this.logger.debug('Loaded widgets: ' + JSON.stringify(this.simpleWidgets));
      },
      (error) => {
        this.logger.error('Error on widget retrieval from server: ' + error);
      },
    );
    return observable;
  }

  public loadAllTags(): Observable<{ data: string[] }> {
    this.allTagsBS.next([]);

    const observable = this.widgetService.getAllTags().pipe(share());

    observable.subscribe(
      (result) => {
        this.allTagsBS.next(result.data);
        this.logger.debug('Loaded tags: ' + JSON.stringify(this.allTags));
        return result.data;
      },
      (error) => {
        this.logger.error('Error on tag retrieval from server: ' + error);
      },
    );
    return observable;
  }

  public loadCustomProfileProperties(): Observable<Array<CustomProfileProperty>> {
    this.customProfilePropertiesBS.next([]);

    const observable = this.widgetService.getCustomProfileProperties().pipe(share());

    observable.subscribe(
      (result) => {
        this.customProfilePropertiesBS.next(result);
        this.logger.debug('Loaded contact fields: ' + JSON.stringify(this.customProfileProperties));
        return result;
      },
      (error) => {
        this.logger.error('Error on CustomProfileProperties retrieval from server: ' + error);
      },
    );
    return observable;
  }

  public loadCustomFonts() {
    this.customFontsBS.next([]);
    this.accountSettingsService.getAllCustomFonts().subscribe(
      (customFonts: Array<AccountCustomFont>) => {
        this.customFontsBS.next(customFonts);
        this.logger.debug('Loaded custom fonts: ' + JSON.stringify(this.customFonts));
      },
      (error) => {
        this.logger.error('Error on custom font retrieval from server: ' + error);
      },
    );
  }

  public loadCampaignById(campaignId: number) {
    if (campaignId) {
      // Clear out objects for new widget
      if (this.campaign) {
        this.clearOutCampaignData();
      }

      this.campaignService.getCampaignById(campaignId).subscribe(
        (campaign: OnsiteCampaign) => {
          this.campaignBS.next(campaign);
          this.logger.debug('Loaded campaign: ' + JSON.stringify(campaign));
        },
        (error) => {
          this.logger.error('Error on campaign retrieval from server: ' + error);
        },
      );
    }
  }

  public setCurrentWidgetStateFromId(widgetStateId: number) {
    this.currentStateId = widgetStateId;
    if (widgetStateId && this.widget && this.widget.states) {
      const currentState = this.widget.states.find((state) => state.id === widgetStateId);
      if (currentState) {
        this.setCurrentWidgetState(currentState);
      }
    }
  }

  public setCurrentWidgetState(widgetState: WidgetState) {
    if (widgetState) {
      widgetState.containers = widgetState.containers.sort((a, b) => a.order - b.order);
      this.widgetStateBS.next(widgetState);
      this.resetVisibleItemIds();
      this.logger.debug('Current state: ' + JSON.stringify(widgetState));
    } else {
      this.logger.debug('Current state cannot be undefined when trying to set it');
      throw Error('Current state cannot be undefined when trying to set it');
    }
  }

  public clearOutWidgetData() {
    this.logger.debug('Clearing out for new widget data');
    this.widgetBS.next(undefined);
    this.widgetStateBS.next(undefined);
    this.resetVisibleItemIds();
    this.currentDeviceViewBS.next(this.defaultDeviceView);
    this.hoveredComponentBS.next(undefined);
    this.updateInProgressBS.next(undefined);
    this.currentStateId = undefined;
    this.sentAutomationSourceByAutomationId.clear();
    this.clearOutCampaignData();
  }

  private clearOutCampaignData() {
    this.logger.debug('Clearing out for new widget data');
    this.campaignBS.next(undefined);
  }

  public addContainerToWidgetState(container: WidgetContainer): Observable<WidgetContainer> {
    const state = this.currentInnerCanvasState ?? this.widgetState;
    container.type = WidgetComponentType.ContainerRegular;
    const lastItem = state.containers.reduce((prev, current) =>
      prev.percentWidth > current.percentWidth ? prev : current,
    );
    // If position is not present, put it at the last position of the state
    if (container.order === undefined) {
      container.order = state.containers
        ? state.containers.map((x) => x.order).reduce((a, b) => Math.max(a, b)) + 1
        : 0;
    }
    const containersToUpdatePercentage = [] as Array<WidgetContainer>;
    if (state.containers && state.containers.length === 1) {
      state.containers.forEach((existingContainer: WidgetContainer) => {
        const containerToUpdate = existingContainer;
        containerToUpdate.percentWidth = 50;
        containersToUpdatePercentage.push(containerToUpdate);
      });
      container.percentWidth = 50;
    } else if (state.containers && state.containers.length > 1) {
      if (
        state.containers.length === 2 &&
        state.containers[0].percentWidth === 50 &&
        state.containers[1].percentWidth === 50
      ) {
        state.containers.forEach((existingContainer: WidgetContainer) => {
          const containerToUpdate = existingContainer;
          containerToUpdate.percentWidth = 33;
          containersToUpdatePercentage.push(containerToUpdate);
        });
        container.percentWidth = 34;
      } else {
        if (lastItem.percentWidth - 10 >= 10) {
          lastItem.percentWidth = lastItem.percentWidth - 10;
          containersToUpdatePercentage.push(lastItem);
        } else {
          return;
        }
      }
    } else {
      container.percentWidth = 100;
    }
    const persist$ = this.widgetService.createWidgetContainer(this.widget.id, state.id, container).pipe(share());

    persist$.subscribe((newContainer: WidgetContainer) => {
      this.widgetService.hasUnpublishedChangesBS.next(true);
      newContainer.type = WidgetComponentType.ContainerRegular;
      newContainer.subType = WidgetComponentSubType.ContainerRegular;
      state.containers.push(newContainer);
      this.setNewCurrentlyEditedComponent(newContainer);

      // Update existing containers percentages
      if (containersToUpdatePercentage.length > 0) {
        containersToUpdatePercentage.forEach((containerToUpdate: WidgetContainer) => {
          this.widgetService.updateWidgetContainer(this.widget.id, state.id, containerToUpdate).subscribe(() => {
            this.widgetService.hasUnpublishedChangesBS.next(true);
            this.logger.debug('Existing container updated');
          });
        });
      }
      this.refreshStateInWidget(state);
      this.resetVisibleItemIds();
    });

    return persist$;
  }

  public updateLayoutAndPosition(
    layoutSettings: WidgetLayout | any,
    previousSettings: WidgetLayout | any,
    position: WidgetPosition,
    singleStateId: number,
    oldLayout: WidgetLayout | any,
    currentProductTourProps,
  ) {
    let newWidthType = undefined;
    if (
      previousSettings &&
      previousSettings?.layout === WidgetLayout.Embedded &&
      layoutSettings.layout !== WidgetLayout.Embedded
    ) {
      newWidthType = 'px';
    } else if (
      previousSettings &&
      previousSettings?.layout !== WidgetLayout.Embedded &&
      layoutSettings.layout === WidgetLayout.Embedded
    ) {
      newWidthType = '%';
      this.widgetTransitionBS.next(null);
    }

    for (let i = 0; i < this.widget.states.length; i++) {
      if (!singleStateId) {
        if (this.widget.states[i].type !== WidgetComponentType.Teaser) {
          this.widget.states[i].position = position;
          this.widget.states[i].layout = undefined;
          if (newWidthType === 'px') {
            this.widget.states[i].width = '600px';
          } else if (newWidthType === '%') {
            this.widget.states[i].width = '100%';
          } else if (layoutSettings.width) {
            this.widget.states[i].width = layoutSettings?.width;
          }
        } else {
          if (newWidthType === 'px') {
            this.widget.states[i].width = '200px';
          } else if (layoutSettings.layout.width) {
            this.widget.states[i].width = layoutSettings?.width;
          }
        }
      } else if (singleStateId === this.widget.states[i].id) {
        this.widget.states[i].layout = layoutSettings.layout;
        this.widget.states[i].position = position;
      }
    }
    this.widgetState.position = position;
    if (singleStateId) {
      this.widgetState.layout = layoutSettings.layout;
    } else {
      this.widgetState.layout = undefined;
    }

    if (layoutSettings.layout && !singleStateId) {
      this.widget.layout = layoutSettings.layout;
      this.widgetBS.next(this.widget);
    }

    const currentComponent = JSON.parse(JSON.stringify(this.currentlyEditedComponent.component));

    if (layoutSettings.layout === WidgetLayout.ProductTour && !currentComponent?.productTourProperties) {
      currentComponent.productTourProperties =
        (currentProductTourProps ? JSON.parse(JSON.stringify(currentProductTourProps)) : null) ||
        this.createProductTourProperties();
    } else if (layoutSettings.layout !== WidgetLayout.ProductTour && currentComponent.productTourProperties) {
      currentComponent.productTourProperties = null;
    }
    currentComponent.layout = layoutSettings.layout;
    currentComponent.position = position;

    if (!currentComponent?.wcagProperties) {
      currentComponent.wcagProperties = {};
    } else if (!currentComponent?.wcagProperties.role) {
      currentComponent.wcagProperties.role = '';
    }

    if (newWidthType === 'px') {
      this.widgetState.width =
        this.widgetState.type === WidgetComponentType.Teaser
          ? '200px'
          : oldLayout?.width && oldLayout.layout !== WidgetLayout.Embedded
            ? oldLayout?.width
            : '600px';
    } else if (newWidthType === '%') {
      this.widgetState.width =
        oldLayout?.width && oldLayout.layout === WidgetLayout.Embedded ? oldLayout?.width : '100%';
    }
    currentComponent.width = this.widgetState.width;
    this.updateCurrentlyEditedComponentBS(
      currentComponent,
      EditedComponentPart.Style,
      EditedComponentSidebarSection.Dimensions,
    );
    this.currentlyEditedComponent.originalComponent.width = currentComponent.width;
    this.widgetStateBS.next(this.widgetState);
  }

  private createProductTourProperties() {
    // Generate initial productTourProperties settings
    const productTourProperties: ProductTourProperties = {
      targetSelector: '.example-selector',
      tourPosition: TourPosition.Left,
      tourAlignment: TourAlignment.Center,
      offsetX: 30,
      offsetY: 0,
      highlightColor:
        this.currentlyEditedComponent?.component?.background?.color ??
        this.widget?.brandVariables?.canvas?.backgroundColor ??
        '#FFFFFF',
      urlMatch: '',
      backDropFilter: null,
      dismissible: false,
      allowInteraction: true,
      zIndex: 1000,
      tourConnectorStyle: TourConnectorStyle.SpeechBubbleTriangle,
      tourConnectorSize: TourConnectorSize.Medium,
      tourConnectorTargetSize: TourConnectorSize.Medium,
      tourConnectorTargetDecoration: [],
      displayEvenIfTargetNotFound: true,
      connectorPosition: 50,
      connectorTargetPosition: 50,
    };
    return productTourProperties;
  }

  public resetLayoutAndPosition(currentWidgetId, widgetsOldLayout, singleStateId, layoutSettings, oldWidgetLayout) {
    for (let i = 0; i < this.widget.states.length; i++) {
      const state = this.widget.states[i];
      const oldLayout = widgetsOldLayout.find((item) => item.id === state.id);
      const isChanged = singleStateId ? state.id === currentWidgetId : state.type !== WidgetComponentType.Teaser;
      if (isChanged) {
        state.position = layoutSettings.position;
        state.layout = singleStateId ? layoutSettings.layout : null;
      } else if (oldLayout) {
        // The teaser is never affected by a widget-wide layout change
        state.position = oldLayout.position;
        state.layout = oldLayout.layout;
        state.width = oldLayout.width;
      }
    }
    this.widget.layout = singleStateId ? oldWidgetLayout : layoutSettings.layout;
    this.syncCurrentStateLayout();
    this.widgetBS.next(this.widget);
  }

  public restoreLayoutsFromSnapshot(
    statesSnapshot: { id: number; layout: WidgetLayout; position: WidgetPosition; width: string }[],
    widgetLayout: WidgetLayout,
    skipStateId?: number,
  ) {
    this.widget.states.forEach((state) => {
      const snapshot = statesSnapshot.find((item) => item.id === state.id);
      if (!snapshot || state.id === skipStateId) {
        return;
      }
      state.layout = snapshot.layout;
      state.position = snapshot.position;
      state.width = snapshot.width;
    });
    this.widget.layout = widgetLayout;
    this.syncCurrentStateLayout();
    this.widgetBS.next(this.widget);
  }

  // After a save widgetState is no longer the same object as its entry in widget.states
  private syncCurrentStateLayout(): void {
    const current = this.widget.states.find((state) => state.id === this.widgetState?.id);
    if (current && current !== this.widgetState) {
      this.widgetState.layout = current.layout;
      this.widgetState.position = current.position;
    }
  }

  public applySavedLayouts(savedWidget: Widget) {
    if (!savedWidget?.states) {
      return;
    }
    this.widget.layout = savedWidget.layout;
    savedWidget.states.forEach((savedState) => {
      const state = this.widget.states.find((s) => s.id === savedState.id);
      if (state) {
        state.layout = savedState.layout;
        state.position = savedState.position;
      }
    });
    this.widgetBS.next(this.widget);
  }

  public addChildComponentToWidgetState(
    component: WidgetComponent,
    position: number,
    containerId: number,
    currentAccount: Account,
    isReplacingPlaceholder: boolean,
    ignoreComponentInheritance?: boolean,
  ) {
    if (position === undefined) {
      throw new Error('Position can not be undefined');
    }
    if (!containerId) {
      throw new Error('ContainerId can not be null');
    }

    const state = this.currentInnerCanvasState ?? this.widgetState;
    const parentComponent = WidgetComponentsService.getParentComponent(state, component.parentId);
    if (!parentComponent) {
      throw new Error('There was no parent for component with parentId: ' + component.parentId);
    }
    const hasButtonComponent = !!parentComponent.children.find((comp) => comp.type === WidgetComponentType.Button);
    const siblingsToUpdateWidthPercentage = [] as Array<WidgetComponent>;
    if (!DataRepeaterService.isDataRepeaterComponent(parentComponent.type)) {
      if (isReplacingPlaceholder !== true) {
        if (!parentComponent.children) {
          parentComponent.children = [];
        }
        const numOfSiblings = parentComponent.children.length;
        parentComponent.children.forEach((sibling: WidgetComponent) => {
          const siblingsToUpdate = JSON.parse(JSON.stringify(sibling));
          siblingsToUpdate.width =
            numOfSiblings === 1
              ? '50%'
              : numOfSiblings === 2
                ? '33%'
                : WidgetComponentHelpers.extractNumberFromPixelOrPercentString(sibling.width) -
                  Math.ceil(10 / numOfSiblings) +
                  '%';
          siblingsToUpdateWidthPercentage.push(siblingsToUpdate);
        });
        component.width =
          numOfSiblings === 0 ? '100%' : numOfSiblings === 1 ? '50%' : numOfSiblings === 2 ? '33%' : '10%';
      } else {
        component.width = parentComponent.children.length > 1 ? parentComponent.children[position].width : '100%';
      }
    } else {
      component.width = '100%';
    }
    if (component.type === WidgetComponentType.InputText && hasButtonComponent) {
      component.label.text = '';
      component.margin = new FourSideStylingEntityWrapper(component.margin).setBottom('8px').getEntity();
    }
    const persist$ = this.addComponentToWidgetState(
      component,
      position,
      containerId,
      currentAccount,
      isReplacingPlaceholder,
      false,
      ignoreComponentInheritance,
      false,
      true,
      true,
      true,
    ).pipe(share());

    persist$.subscribe(() => {
      setTimeout(() => {
        // Update existing containers percentages
        if (
          siblingsToUpdateWidthPercentage.length > 0 &&
          !DataRepeaterService.isDataRepeaterComponent(parentComponent.type)
        ) {
          siblingsToUpdateWidthPercentage.forEach((sibling: WidgetComponent) => {
            this.widgetService.updateWidgetComponent(this.widget.id, sibling).subscribe(
              () => {
                const parent = WidgetComponentsService.getParentComponent(this.widgetState, component.parentId);
                parent.children.find((child) => child.id === sibling.id).width = sibling.width;
                this.forceRefreshOnComponent(sibling);
              },
              () => {
                this.snackbarService.error('generic.error');
              },
            );
          });
        }
        // The edited parent is a clone diverged from widgetState; re-sync it or the new child won't render until reload.
        this.forceRefreshOnComponent(parentComponent);
      }, 200);
    });
    return persist$;
  }

  private postProcessNewFloatingComponentInState(
    floatingComponent: WidgetComponent,
    floatingContainer: WidgetContainer,
    isCloseEditorComponent?,
  ) {
    if (floatingContainer.components) {
      floatingContainer.components.push(floatingComponent);
    } else {
      floatingContainer.components = [floatingComponent];
    }
    this.refreshStateInWidget(this.widgetState);
    this.resetVisibleItemIds();
    isCloseEditorComponent
      ? this.setNewCurrentlyEditedComponent(this.widgetState, isCloseEditorComponent)
      : this.setNewCurrentlyEditedComponent(floatingComponent, isCloseEditorComponent);
  }

  private checkAndAddSubmitButton(
    latestAddedComponent: WidgetComponent,
    containerId: number,
    currentAccount: Account,
    widgetState,
  ) {
    const componentTypesToCheck = [
      WidgetComponentType.InputText,
      WidgetComponentType.Checkbox,
      WidgetComponentType.Dropdown,
      WidgetComponentType.Date,
      WidgetComponentType.ButtonList,
    ];
    const childHasType = latestAddedComponent.children?.find((x) => componentTypesToCheck.indexOf(x.type) !== -1);
    const isSubmitButtonExistedInGroup = latestAddedComponent.children?.find(
      (x) => x.subType === WidgetComponentSubType.ButtonSubmit,
    );
    if (
      ((componentTypesToCheck.indexOf(latestAddedComponent.type) !== -1 &&
        latestAddedComponent.subType !== WidgetComponentSubType.ButtonListChoices) ||
        (childHasType && childHasType.subType !== WidgetComponentSubType.ButtonListChoices)) &&
      !isSubmitButtonExistedInGroup &&
      !WidgetComponentHelpers.hasWidgetSubmitButton(this.widget) &&
      !WidgetComponentHelpers.hasWidgetSubmitButton({ ...this.widget, states: [widgetState] })
    ) {
      const position = widgetState.containers.find((c) => c.id === containerId).components.length;
      const component = WidgetComponentHelpers.createBlankWidgetComponentFromSubtype(
        this.widget,
        WidgetComponentSubType.ButtonSubmit,
        this.languageService,
        false,
        currentAccount,
        undefined,
        undefined,
        this.accountLanguageService,
      );
      component.order = position;

      WidgetComponentHelpers.updateComponentsLayoutFromSimilarComponents(
        component,
        widgetState,
        this.widget,
        this.languageService,
      );

      const persist$ = this.widgetService
        .createWidgetComponent(this.widget.id, widgetState.id, containerId, component, position)
        .pipe(share());

      persist$.subscribe((responseComponent: WidgetComponent) => {
        this.logger.debug('Component is created inside of widgetEditorData: ' + JSON.stringify(responseComponent));
        this.widgetService.hasUnpublishedChangesBS.next(true);
        // If everything went well send UNDEFINED as currently edited component
        if (responseComponent) {
          responseComponent.containerId = containerId;
        }

        if (!widgetState.containers.find((cont) => cont.id === containerId).components) {
          widgetState.containers.find((cont) => cont.id === containerId).components = [responseComponent];
        } else {
          widgetState.containers
            .find((cont) => cont.id === containerId)
            .components.splice(position, 0, responseComponent);
        }
        this.refreshStateInWidget(widgetState);
        this.resetVisibleItemIds();
        this.actionOnComponentBS.next({ action: ActionOnComponent.Add, component: responseComponent });
      });
    }
  }

  public addComponentToWidgetState(
    component: WidgetComponent,
    position: number,
    containerId: number,
    currentAccount: Account,
    isReplacingPlaceholder: boolean,
    isCloseEditorComponent?: boolean,
    ignoreComponentInheritance: boolean = false,
    openAddWindow: boolean = false,
    addSubmitButton: boolean = true,
    setComponentAsCurrent: boolean = true,
    addToGroup: boolean = false,
    sourceAutomationId?: string,
  ) {
    const state = this.currentInnerCanvasState ?? this.widgetState;
    // If container is not present, put it at the last position of the last container
    if (!containerId) {
      const onlyVisibleContainers =
        this.currentDeviceView === WidgetDeviceView.Mobile
          ? state.containers.filter((container) => !container.hideOnMobile)
          : state.containers.filter((container) => !container.hideOnDesktop);
      containerId = onlyVisibleContainers.length
        ? onlyVisibleContainers[onlyVisibleContainers.length - 1].id
        : state.containers[state.containers.length - 1].id;
    }
    if (position === undefined) {
      const componentsInContainer = state.containers.find((cont) => cont.id === containerId).components;
      const submitButton = componentsInContainer.findIndex((x) => x.subType === WidgetComponentSubType.ButtonSubmit);
      const checkboxConsent = componentsInContainer.findIndex(
        (x) => x.subType === WidgetComponentSubType.CheckboxConsent,
      );
      if (!componentsInContainer || componentsInContainer.length === 0) {
        position = 0;
      } else if (component.subType === WidgetComponentSubType.CheckboxConsent && submitButton !== -1) {
        position = submitButton;
      } else {
        position = componentsInContainer.length;
      }
      if (submitButton !== -1 && component.subType !== WidgetComponentSubType.CheckboxConsent) {
        if (submitButton !== -1 && checkboxConsent !== -1 && checkboxConsent + 1 === submitButton) {
          position = checkboxConsent;
        } else {
          position = submitButton;
        }
      }
      component.order = position;
    }

    // If component is submit button and there is a success state, pre-set this success state on submit
    if (component.subType === WidgetComponentSubType.ButtonSubmit) {
      const successState = this.widget.states.find((state) => state.type === WidgetComponentType.Success);
      if (successState) {
        (component as ButtonComponent).actionValue = successState.id + '';
        (component as ButtonComponent).actionType = ButtonActionType.SwitchState;
      }
    }

    if (
      component.subType === WidgetComponentSubType.ButtonListChoices &&
      component.listItems?.filter((item) => item.isCorrect).length > 1
    ) {
      (component as ButtonListComponent).buttonListProperties.allowMultipleSelection = true;
    }

    // If component is a ShareCampaign we set default share-text
    if (component.type === WidgetComponentType.ShareCampaign) {
      component.referFriendProperties = {
        message: this.languageService.translateInstantly(
          'campaign.widget.component.share-campaign.message-default-text',
        ),
        path: null,
      };
    }

    // If the component subtype is a ButtonListSubscriptionCheckbox and only one integration is available, set that value.
    if (component.subType === WidgetComponentSubType.ButtonListSubscriptionCheckbox) {
      this.integrationsService.activeIntegrationsBS.subscribe((integrations) => {
        if (integrations) {
          const availableSourceIntegrations: { key?: IntegrationType; translation?: string; iconPath?: string }[] = [];
          const activeIntegrations = integrations.map((x) => IntegrationsService.convertOldIntegrationTypes(x.type));
          for (const integration of activeIntegrations) {
            if (WidgetComponentHelpers.subscriptionCheckboxIntegrations.includes(integration)) {
              availableSourceIntegrations.push({
                key: integration,
                translation: IntegrationsService.getDataSourceTranslationKey(integration),
                iconPath: IntegrationsService.getIconPath(integration, true),
              });
            }
          }
          if (availableSourceIntegrations.length === 1) {
            if (component.subscriptionCheckboxProperties === undefined) {
              component.subscriptionCheckboxProperties = {
                sourceIntegration: availableSourceIntegrations[0].key,
              };
            }
          }
        }
      });
    }

    // If component is a floating component ensure there is a floating container first
    if (WidgetComponentHelpers.isFloatingComponent(component)) {
      return this.getOrCreateFloatingContainer().pipe(
        switchMap((floatingContainer: WidgetContainer) => {
          state.floatingContainer = floatingContainer;

          if (!ignoreComponentInheritance) {
            WidgetComponentHelpers.updateComponentsLayoutFromSimilarComponents(
              component,
              state,
              this.widget,
              this.languageService,
            );
          }

          const persist$ = this.widgetService
            .createWidgetComponent(this.widget.id, state.id, floatingContainer.id, component, 0, sourceAutomationId)
            .pipe(share());

          persist$.subscribe((responseComponent: WidgetComponent) => {
            this.widgetService.hasUnpublishedChangesBS.next(true);
            responseComponent.containerId = containerId;
            this.postProcessNewFloatingComponentInState(responseComponent, floatingContainer, false);
            this.actionOnComponentBS.next({ action: ActionOnComponent.Add, component: responseComponent });
          });
          return persist$;
        }),
        catchError(() => {
          this.snackbarService.error(
            'campaign.widget.container-save-error',
            undefined,
            5000,
            SnackBarPosition.BottomLeft,
          );
          return of(undefined);
        }),
      );
    } else {
      if (!ignoreComponentInheritance) {
        WidgetComponentHelpers.updateComponentsLayoutFromSimilarComponents(
          component,
          state,
          this.widget,
          this.languageService,
          addToGroup,
        );
      }

      const persist$ = this.widgetService
        .createWidgetComponent(this.widget.id, state.id, containerId, component, position, sourceAutomationId)
        .pipe(share());
      persist$.subscribe((responseComponent: WidgetComponent) => {
        this.logger.debug('Component is created inside of widgetEditorData: ' + JSON.stringify(responseComponent));
        this.widgetService.hasUnpublishedChangesBS.next(true);
        // If everything went well send UNDEFINED as currently edited component
        if (responseComponent) {
          responseComponent.containerId = containerId;
          WidgetComponentsService.setChildrenProperties(responseComponent, containerId);
          if (!component.parentId) {
            if (position !== undefined) {
              if (!state.containers.find((cont) => cont.id === containerId).components) {
                state.containers.find((cont) => cont.id === containerId).components = [responseComponent];
              } else {
                state.containers
                  .find((cont) => cont.id === containerId)
                  .components.splice(position, 0, responseComponent);
              }
            } else {
              state.containers
                .find((containerToCheck) => containerToCheck.id === containerId)
                .components.push(responseComponent);
            }
          } else {
            responseComponent.parentId = component.parentId;
            const parent = WidgetComponentsService.getParentComponent(state, component.parentId);
            responseComponent.parentComponentType = parent.type;
            if (isReplacingPlaceholder === true) {
              // Replace placeholder with correct component
              parent.children[position] = responseComponent;
            } else if (position !== undefined) {
              if (!parent.children) {
                parent.children = [responseComponent];
              } else {
                parent.children.splice(position, 0, responseComponent);
              }
            } else {
              parent.children.push(responseComponent);
            }
          }

          state.containers.forEach((container) => {
            if (container.components) {
              container.components.forEach((comp, index) => {
                comp.order = index;
              });
            }
          });

          if (addSubmitButton) {
            this.checkAndAddSubmitButton(responseComponent, containerId, currentAccount, state);
          }

          this.logger.debug('AFTER ADDED COMPONENT: ' + JSON.stringify(state));
          this.refreshStateInWidget(state);
          this.resetVisibleItemIds();
          this.forceRefreshOnComponent(responseComponent);
          this.actionOnComponentBS.next({ action: ActionOnComponent.Add, component: responseComponent });
          if (setComponentAsCurrent) {
            this.setNewCurrentlyEditedComponent(responseComponent, false, openAddWindow);
          }
          this.updateIdentifiedEmailMarketingCondition(component);
          // Switch to mobile view when adding a component that is not visible on desktop
          if (this.isComponentAllowRealTime(responseComponent)) {
            this.modifyAutomationInRealTime(currentAccount, responseComponent);
          }
          if (component.hideOnDesktop) {
            this.setCurrentDeviceView(WidgetDeviceView.Mobile);
          }
        } else {
          throw new Error(
            'There was no component returned after successful create: ' + JSON.stringify(responseComponent),
          );
        }
      });
      return persist$;
    }
  }

  private getOrCreateFloatingContainer(): Observable<WidgetContainer> {
    if (this.widgetState.floatingContainer) {
      if (!this.widgetState.floatingContainer.components) {
        this.widgetState.floatingContainer.components = [];
      }
      return of(this.widgetState.floatingContainer);
    } else {
      const container = {
        type: WidgetComponentType.ContainerFloating,
        components: [],
      } as WidgetContainer;
      return this.widgetService.createWidgetContainer(this.widget.id, this.widgetState.id, container);
    }
  }

  public updateCurrentlyEditedComponentBS(
    component: WidgetComponent,
    latestUpdatedPart: EditedComponentPart,
    sidebarSection: EditedComponentSidebarSection,
  ) {
    if (!component) {
      this.logger.debug('Undefined component send to update in BS, ignored (should not happen)');
      return;
    }
    const componentIsTheSame =
      this.currentlyEditedComponent &&
      this.currentlyEditedComponent.component &&
      component.type === this.currentlyEditedComponent.component.type &&
      component.id === this.currentlyEditedComponent.component.id;
    if (!componentIsTheSame) {
      this.logger.debug(
        'Different component send to update in BS than it was before, ignored (should not happen). Component: ' +
          JSON.stringify(component),
      );
      return;
    }
    const currentComponent = this.currentlyEditedComponent
      ? JSON.parse(JSON.stringify(this.currentlyEditedComponent))
      : ({ originalComponent: component } as EditModeComponent);
    currentComponent.component = component;
    // Set latestUpdatedPart if present (text or style)
    if (latestUpdatedPart) {
      currentComponent.latestUpdatedPart = latestUpdatedPart;
    }
    currentComponent.sidebarSection = sidebarSection;
    this.logger.debug('Pushing same but updated component in BS: ' + JSON.stringify(currentComponent));
    this.isCurrentTabChangedBS.next(false);
    this.currentlyEditedComponentBS.next(currentComponent);
    if (DataRepeaterService.isComponentInsideDataRepeater(component)) {
      this.dataRepetearShowCopiesBS.next(true);
    }
  }

  public cancelCurrentlyEditedComponent() {
    this.logger.debug(
      'Canceling currently edited component: ' +
        (this.currentlyEditedComponent && this.currentlyEditedComponent.component
          ? this.currentlyEditedComponent.component.id
          : undefined),
    );
    // Setting back original component before removing it from BS
    if (this.currentlyEditedComponent && this.currentlyEditedComponent.originalComponent) {
      this.currentlyEditedComponentBS.next({
        component: this.currentlyEditedComponent.originalComponent,
      } as EditModeComponent);
    }
    this.currentlyEditedComponentBS.next(undefined);
  }

  public discardInvalidEdits(): void {
    // Save-on-switch paths land here when the component failed validation and can't be persisted.
    // The revert is unavoidable, but warn so the discard isn't silent — a bare cancel reads as "my changes vanished".
    if (!this.isSaveable && this.isCurrentComponentUpdated()) {
      this.snackbarService.warning('campaign.widget.reverted-invalid-changes');
    }
    this.cancelCurrentlyEditedComponent();
  }

  private refreshCurrentlyEditedComponent(component: any, isCloseEditorComponent?: boolean) {
    const currentComponent = this.currentlyEditedComponent;
    if (component && currentComponent && currentComponent?.component?.id === component.id) {
      // Keep the working copy: re-emitting the saved snapshot would clobber edits made mid-save and clear
      // the dirty flag, losing them. Advance the baseline only on a real content change — compared without
      // `$type`, which differs between the request and in-editor response DTOs and must not read as an edit.
      const withoutType = (entity: any) =>
        JSON.stringify(entity, (key, value) => (key === '$type' ? undefined : value));
      const editedDuringSave = withoutType(currentComponent.component) !== withoutType(component);
      const baseline = editedDuringSave ? component : currentComponent.component;
      this.currentlyEditedComponentBS.next({
        component: JSON.parse(JSON.stringify(currentComponent.component)),
        originalComponent: JSON.parse(JSON.stringify(baseline)),
        isCloseEditorComponent,
      } as EditModeComponent);
    }
  }

  public currentPrimaryCanvasState: WidgetState;
  public currentInnerCanvasState: WidgetState;

  public setNewCurrentlyEditedComponent(
    component: any,
    isCloseEditorComponent?: boolean,
    openAddWindow = false,
    skipPersistCheck = false,
  ) {
    if (WidgetComponentHelpers.isComponentNoNeedEdit(component)) {
      return;
    }
    this.logger.debug(
      'A request for setting new currently edited component is sent...' +
        (component ? component.id : 'it was undefined'),
    );
    if (component) {
      // Check if it was switching from another component, in that case save the previous one
      if (
        this.currentlyEditedComponent &&
        this.currentlyEditedComponent.component &&
        component.id !== this.currentlyEditedComponent.component.id &&
        !skipPersistCheck
      ) {
        this.originalComponent = {
          ...this.currentlyEditedComponent.originalComponent,
        };
        this.persistEditedComponent(this.currentlyEditedComponent.component, false, null, true);
      }
      if (openAddWindow) {
        this.currentlyEditedComponentBS.next({
          component: undefined,
          originalComponent: undefined,
          sidebarSection: EditedComponentSidebarSection.AddAfterCopy,
          isCloseEditorComponent,
        } as EditModeComponent);
      } else {
        this.currentlyEditedComponentBS.next({
          component: JSON.parse(JSON.stringify(component)),
          originalComponent: JSON.parse(JSON.stringify(component)),
          isCloseEditorComponent,
        } as EditModeComponent);
      }
      if (this.dataRepetearShowCopiesBS.getValue()) {
        this.dataRepetearShowCopiesBS.next(true);
      }
    } else {
      this.currentlyEditedComponentBS.next(undefined);
    }
  }

  // Points the open component, step or container at its copy in the reloaded widget, with the user's unsaved edits
  // re-applied, so the next save carries both. Returns true when both changed the same value.
  public reapplyUnsavedEdits(edited: EditModeComponent | undefined, widget: Widget): boolean {
    if (!edited?.component || !edited.originalComponent) {
      return false;
    }
    const reloaded = this.findInWidget(widget, edited.component);
    if (!reloaded) {
      this.currentlyEditedComponentBS.next(undefined);
      return false;
    }
    const { merged, conflict } = mergeUnsavedEdits<EditableItem>(edited.originalComponent, edited.component, reloaded);
    this.currentlyEditedComponentBS.next({
      ...edited,
      component: merged as WidgetComponent,
      originalComponent: JSON.parse(JSON.stringify(reloaded)),
    });
    return conflict;
  }

  private findInWidget(widget: Widget, item: EditableItem): EditableItem | undefined {
    const states = widget.states ?? [];
    if (WidgetComponentHelpers.isWidgetState(item)) {
      return states.find((state) => state.id === item.id);
    }
    if (WidgetComponentHelpers.isWidgetContainer(item)) {
      return states
        .flatMap((state) => [...state.containers, ...(state.floatingContainer ? [state.floatingContainer] : [])])
        .find((container) => container.id === item.id);
    }
    const state = WidgetStatesService.getStateByComponentId(states, item.id);
    return state ? WidgetComponentsService.getComponentById(state, item.id) : undefined;
  }

  public isCurrentComponentUpdated(): boolean {
    const edited = this.currentlyEditedComponent;
    return (
      !!edited?.component &&
      JSON.stringify(withoutEmptyValues(edited.component)) !==
        JSON.stringify(withoutEmptyValues(edited.originalComponent))
    );
  }

  public removeComponent(component: any): Observable<WidgetComponent> {
    // A second click before the delete is done would delete it again, and fail.
    if (this.beingRemoved.has(component)) {
      return EMPTY;
    }
    this.beingRemoved.add(component);
    let remove$;
    // Captured before the delete: afterwards the component is gone from the widget and its links with it.
    const linkedAutomationIds = this.collectLinkedAutomationIds(component);
    // If State
    if (WidgetComponentHelpers.isWidgetState(component)) {
      remove$ = this.widgetService.deleteWidgetState(this.widget.id, component.id).pipe(share());
      remove$.subscribe(() => {
        this.widgetService.hasUnpublishedChangesBS.next(true);
        // If everything went well send UNDEFINED as currently edited component
        const stateIndex = this.widget.states.findIndex((st) => st.id === component.id);
        this.widget.states.splice(stateIndex, 1);
        // Re-order states
        this.widget.states.forEach((state, index) => {
          state.order = index;
        });
        // Must run after the reindex above, or it would PUT each state's stale pre-deletion order.
        this.unlinkInnerStatesFromPrimary(component.id);
        this.currentlyEditedComponentBS.next(undefined);
        if (this.widget.states.length > 0) {
          this.widgetStateBS.next(this.widget.states[0]);
        } else {
          this.widgetStateBS.next(undefined);
        }
        this.actionOnComponentBS.next({ action: ActionOnComponent.Remove, component });
        this.resetVisibleItemIds();
      });
      // If container
    } else if (component.type === WidgetComponentType.ContainerRegular) {
      const state = WidgetStatesService.getStateByContainerId(this.widget.states, component.id);
      remove$ = this.widgetService.deleteWidgetContainer(this.widget.id, state.id, component.id).pipe(
        tap(() => this.widgetStateBS.next(state)),
        share(),
      );
      remove$.subscribe(() => {
        this.widgetService.hasUnpublishedChangesBS.next(true);
        // If everything went well send UNDEFINED as currently edited component
        const containerIndex = state.containers.findIndex((cont) => cont.id === component.id);
        this.updateWidgetContainerOrder(state.containers, component.order);
        this.widgetService.hasUnpublishedChangesBS.next(true);
        state.containers.splice(containerIndex, 1);
        this.refreshStateInWidget(state);
        this.currentlyEditedComponentBS.next(undefined);
        this.resetVisibleItemIds();
      });
      // If regular component
    } else {
      const state = WidgetStatesService.getStateByComponentId(this.widget.states, component.id);
      this.unsetPrimaryCanvasOnComponentRemove(component);
      remove$ = this.widgetService
        .deleteWidgetComponent(this.widget.id, component.containerId, component.id)
        .pipe(share());
      remove$.subscribe(() => {
        this.widgetService.hasUnpublishedChangesBS.next(true);
        // If everything went well send UNDEFINED as currently edited component
        const containerIndex = state.containers.findIndex((cont) => cont.id === component.containerId);
        if (WidgetComponentHelpers.isFloatingComponent(component) === true) {
          // Component in floating container deleted
          const componentIndex = state.floatingContainer.components.findIndex((comp) => comp.id === component.id);
          state.floatingContainer.components.splice(componentIndex, 1);
        } else if (!component.parentId) {
          // Top-level component deleted
          const componentIndex = state.containers[containerIndex].components.findIndex(
            (comp) => comp.id === component.id,
          );
          state.containers[containerIndex].components.splice(componentIndex, 1);
        } else {
          // A child component was deleted
          const parent = WidgetComponentsService.getParentComponent(state, component.parentId);
          const componentIndex = parent.children.findIndex((comp) => comp.id === component.id);
          parent.children.splice(componentIndex, 1);
          if (parent.children.length > 0 && !DataRepeaterService.isDataRepeaterComponent(parent.type)) {
            this.updateChildComponentsWidthToFitParent(this.widget.id, state.id, parent.children);
          }
          if (DataRepeaterService.isDataRepeaterComponent(parent.type)) {
            this.forceRefreshOnComponent(parent);
          }
        }
        const automationId = this.widget.states.find((state) => state.automationId)?.automationId;
        if (automationId) {
          this.deleteRelatedAutomation(component, automationId);
        }
        this.refreshStateInWidget(state);
        this.setNewCurrentlyEditedComponent(state, false, false, true);
        if (!state.isInnerCanvasState) {
          this.widgetStateBS.next(state);
        } else {
          this.currentInnerCanvasState = state;
        }
        this.resetVisibleItemIds();
        this.updateIdentifiedEmailMarketingCondition(component);
        if (this.isComponentAllowRealTime(component)) {
          this.modifyAutomationInRealTime(this.currentContextService.currentContext.account, component);
        }
        this.actionOnComponentBS.next({ action: ActionOnComponent.Remove, component });
      });
    }
    if (linkedAutomationIds.length) {
      // Same offer as unticking the checkbox: the automation outlives what it was linked to.
      remove$.subscribe(() => this.confirmUnlinkedAutomationDeletion(linkedAutomationIds));
    }
    remove$.subscribe({ error: () => undefined }).add(() => this.beingRemoved.delete(component));
    return remove$;
  }

  // Everything being removed in one go: the entity itself, its children, and for a state or container
  // everything nested inside it.
  private collectLinkedAutomationIds(component: any): string[] {
    const ids: string[] = [];
    const visit = (item: any) => {
      if (!item) {
        return;
      }
      if (item.actionAutomationId) {
        ids.push(item.actionAutomationId);
      }
      // A coupon component also owns the automation its distribution fires.
      if (item.impressionAutomationId) {
        ids.push(item.impressionAutomationId);
      }
      (item.children ?? []).forEach(visit);
      (item.components ?? []).forEach(visit);
      (item.containers ?? []).forEach(visit);
      if (item.floatingContainer) {
        visit(item.floatingContainer);
      }
    };
    visit(component);
    return ids;
  }

  private unsetPrimaryCanvasOnComponentRemove(component: WidgetComponent) {
    if (component.type === WidgetComponentType.StepContainer && this.widgetState?.isPrimaryCanvasState) {
      // If we remove the step container from a primary canvas state, it is no longer primary
      this.unlinkInnerStatesFromPrimary(this.widgetState.id);
      this.widgetBS.next(this.widget);
    }
  }

  // Detach states still pointing at a primary canvas state that no longer acts as one.
  private unlinkInnerStatesFromPrimary(primaryCanvasStateId: number) {
    for (const state of this.widget.states) {
      if (state.primaryCanvasStateId === primaryCanvasStateId) {
        state.primaryCanvasStateId = undefined;
        state.isPrimaryCanvasState = false;
        state.isInnerCanvasState = false;
        this.widgetService.updateWidgetState(state.widgetId, state).subscribe(() => {
          this.logger.info('primaryCanvasStateId unset on state', state.id);

          // Update widget state in widget
          this.refreshStateInWidget(state);
        });
      }
    }
  }

  public deleteRelatedAutomation(component: WidgetComponent, automationId: string) {
    let mergeVar = '';
    let shouldSave = false;
    if (component.subType === 'InputTextName') {
      mergeVar = '{{contact.name}}';
    } else if (component.subType === 'InputTextFirstName') {
      mergeVar = '{{contact.firstname}}';
    } else if (component.subType === 'InputTextLastName') {
      mergeVar = '{{contact.lastname}}';
    } else if (component.subType === 'InputTextPhone') {
      mergeVar = '{{contact.telephone}}';
    } else {
      mergeVar = '{{session.custom.' + component.name + '}}';
    }
    let automation;
    let newAutomation;
    this.automationService
      .getAutomation(automationId)
      .then((result) => {
        automation = result;
        newAutomation = automation.actions.slice();
        for (const action of automation.actions) {
          if (action.id.endsWith('UpdateMergeTag')) {
            const argument = action.arguments.find((x) => x.value === mergeVar);
            if (argument) {
              const index = newAutomation.findIndex((x) => x === action);
              newAutomation.splice(index, 1);
              shouldSave = true;
            }
          } else if (action.id.endsWith('AddEmailToList')) {
            if (component.subType === 'InputTextEmail') {
              const index = newAutomation.findIndex((x) => x === action);
              newAutomation.splice(index, 1);
              shouldSave = true;
            }
          }
        }
        if (shouldSave) {
          automation.actions = newAutomation;
          this.createAutomationService.saveAutomation(automation);
        }
      })
      .catch(() => {});
  }

  // Decrease order of containers to right of deleted one
  private updateWidgetContainerOrder(containers: WidgetContainer[], order: number) {
    containers.filter((c) => c.order > order).forEach((c) => c.order--);
  }

  private updateChildComponentsWidthToFitParent(widgetId: number, stateId: number, children: Array<WidgetComponent>) {
    let childrensWidthBefore = 0;
    const childrensWidths = [] as Array<number>;

    children.forEach((child) => {
      childrensWidths.push(WidgetComponentHelpers.extractNumberFromPixelOrPercentString(child.width));
      childrensWidthBefore += WidgetComponentHelpers.extractNumberFromPixelOrPercentString(child.width);
    });
    const toAddOnEachChild = Math.floor((100 - childrensWidthBefore) / children.length);

    for (let i = 0; i < childrensWidths.length; i++) {
      childrensWidths[i] = childrensWidths[i] + toAddOnEachChild;
    }
    const toAdjustOnLastChild = 100 - (childrensWidthBefore + childrensWidths.length * toAddOnEachChild);
    if (toAdjustOnLastChild !== 0) {
      childrensWidths[childrensWidths.length - 1] = childrensWidths[childrensWidths.length - 1] + toAdjustOnLastChild;
    }

    for (let i = 0; i < children.length; i++) {
      children[i].width = childrensWidths[i] + '%';
      this.widgetService.updateWidgetComponent(widgetId, children[i]).subscribe(() => {
        const state = this.widget.states.find((st) => st.id === stateId);
        const parent = WidgetComponentsService.getParentComponent(state, children[i].parentId);
        parent.children.find((child) => child.id === children[i].id).width = children[i].width;
        this.forceRefreshOnComponent(children[i]);
      });
    }
  }

  public updateAutomationIdOnCurrentState(automationId: string, widgetState): Observable<WidgetState> {
    const persist$ = this.widgetService.updateWidgetState(widgetState.widgetId, widgetState).pipe(share());
    persist$.subscribe(() => {
      this.widgetService.hasUnpublishedChangesBS.next(true);
      widgetState.automationId = automationId;
      // this.widgetStateBS.next(widgetState);
      this.refreshStateInWidget(widgetState);
    });
    return persist$;
  }

  set updateInProgress(updateInProgress: WidgetUpdateProgress) {
    this.updateInProgressBS.next({
      value: updateInProgress,
      messages: 'campaign.widget.updateInProgress-up-to-date',
    });
  }

  public implementSameStylingAndSettingsOnAllStates(
    originalState: WidgetState,
    differencesInStyle,
  ): Observable<WidgetState[]> {
    const statesToUpdate = this.widget.states.filter(
      (s) => s.id !== originalState.id && s.type !== WidgetComponentType.Teaser,
    );
    if (statesToUpdate.length > 0) {
      const requests$ = [];
      for (let i = 0; i < statesToUpdate.length; i++) {
        const state = !differencesInStyle
          ? WidgetComponentHelpers.updateWidgetStateStylingAndSettingsFromExisting(statesToUpdate[i], originalState)
          : WidgetComponentHelpers.updateWidgetStateSingleStyleAndSettingsFromExisting(
              statesToUpdate[i],
              originalState,
              differencesInStyle,
            );

        requests$.push(this.widgetService.updateWidgetState(this.widget.id, state));
      }
      return forkJoin(requests$);
    } else {
      return of([]);
    }
  }

  public implementSingleStyleOnAllStates(originalState: WidgetComponent, props, skipOriginal = false): Observable<any> {
    const statesToUpdate = this.widget.states.filter(
      (s) => (skipOriginal ? s.id !== originalState.id : s.id) && s.type !== WidgetComponentType.Teaser,
    );
    if (statesToUpdate.length > 0) {
      const requests$ = [];
      for (let i = 0; i < statesToUpdate.length; i++) {
        props.forEach((prop) => (statesToUpdate[i][prop] = originalState[prop]));
        requests$.push(this.widgetService.updateWidgetState(this.widget.id, statesToUpdate[i]));
      }
      return forkJoin(requests$);
    } else {
      return of([]);
    }
  }

  public implementSameStylingOnAllButtons(originalButton: WidgetComponent, differencesInStyle): Observable<any> {
    const requests$ = [];
    for (let i = 0; i < this.widget.states.length; i++) {
      let buttonsToUpdate = [];
      if (originalButton.subType === WidgetComponentSubType.ButtonInstagramFollow) {
        buttonsToUpdate = WidgetComponentHelpers.getAllComponentsOnState(this.widget.states[i]).filter(
          (c) => c.subType === WidgetComponentSubType.ButtonInstagramFollow && c.id !== originalButton.id,
        );
      } else {
        buttonsToUpdate = WidgetComponentHelpers.getAllComponentsOnState(this.widget.states[i]).filter(
          (c) =>
            c.type === WidgetComponentType.Button &&
            c.subType !== WidgetComponentSubType.ButtonHide &&
            (c.parentComponentType ? c.parentComponentType !== WidgetComponentType.DiscountCode : true) &&
            c.id !== originalButton.id &&
            c.subType !== WidgetComponentSubType.ButtonInstagramFollow,
        );
      }
      if (buttonsToUpdate.length > 0) {
        buttonsToUpdate.forEach((button) => {
          const updated = differencesInStyle
            ? WidgetComponentHelpers.updateComponentsSingleStyleFromExisting(
                button,
                originalButton,
                this.languageService,
                this.widget,
                differencesInStyle,
              )
            : WidgetComponentHelpers.updateComponentsStylingFromExisting(
                button,
                originalButton,
                this.languageService,
                this.widget,
                null,
              );
          requests$.push(this.widgetService.updateWidgetComponent(this.widget.id, updated));
        });
      }
    }
    return requests$.length > 0 ? forkJoin(requests$) : of([]);
  }

  generateRequestForFormComponents(originalButton, buttonListsToUpdate, state, requests$) {
    const formComponentsToUpdate = WidgetComponentHelpers.getAllComponentsOnState(state).filter(
      (c) =>
        (c.type === WidgetComponentType.ButtonList ||
          c.type === WidgetComponentType.InputText ||
          c.type === WidgetComponentType.Date ||
          c.type === WidgetComponentType.Dropdown ||
          c.type === WidgetComponentType.InternationalPhone) &&
        c.id !== originalButton.id &&
        !buttonListsToUpdate.some((b) => b.id === c.id),
    );
    if (formComponentsToUpdate.length > 0) {
      formComponentsToUpdate.forEach((c) =>
        requests$.push(
          this.widgetService.updateWidgetComponent(
            this.widget.id,
            WidgetComponentHelpers.updateLabelStylingFromExisting(c, originalButton),
          ),
        ),
      );
    }
  }

  public implementSameStylingOnAllButtonLists(originalButton: WidgetComponent, diiferencesInStyle): Observable<any> {
    const requests$ = [];
    for (let i = 0; i < this.widget.states.length; i++) {
      const buttonListsToUpdate = WidgetComponentHelpers.getAllComponentsOnState(this.widget.states[i]).filter(
        (c) =>
          c.type === WidgetComponentType.ButtonList &&
          c.subType === originalButton.subType &&
          c.id !== originalButton.id,
      );
      if (buttonListsToUpdate.length > 0) {
        buttonListsToUpdate.forEach((button) => {
          const updated = diiferencesInStyle
            ? WidgetComponentHelpers.updateButtonListSingleStyleFromExisting(
                button,
                [originalButton],
                diiferencesInStyle,
              )
            : WidgetComponentHelpers.updateButtonListStylingFromExisting(button, [originalButton], undefined, true);
          requests$.push(this.widgetService.updateWidgetComponent(this.widget.id, updated));
        });
      }
      this.generateRequestForFormComponents(originalButton, buttonListsToUpdate, this.widget.states[i], requests$);
    }
    return requests$.length > 0 ? forkJoin(requests$) : of([]);
  }

  public implementSameStylingOnAllCloseButtons(originalButton: WidgetComponent, diiferencesInStyle): Observable<any> {
    const requests$ = [];
    for (let i = 0; i < this.widget.states.length; i++) {
      const closeButtonsToUpdate = WidgetComponentHelpers.getAllComponentsOnState(this.widget.states[i]).filter(
        (c) =>
          c.type === WidgetComponentType.Button &&
          c.subType === WidgetComponentSubType.ButtonHide &&
          c.id !== originalButton.id,
      );
      if (closeButtonsToUpdate.length > 0) {
        closeButtonsToUpdate.forEach((button) => {
          const updated = diiferencesInStyle
            ? WidgetComponentHelpers.updateCloseButtonSingleStyleFromExisting(
                button,
                originalButton,
                diiferencesInStyle,
              )
            : WidgetComponentHelpers.updateCloseButtonStylingFromExisting(button, originalButton);
          requests$.push(this.widgetService.updateWidgetComponent(this.widget.id, updated));
        });
      }
    }
    return requests$.length > 0 ? forkJoin(requests$) : of([]);
  }

  public implementSameStylingOnAllInputFields(
    originalField: WidgetComponent,
    widget,
    diiferencesInStyle,
  ): Observable<any> {
    const requests$ = [];
    for (let i = 0; i < this.widget.states.length; i++) {
      const inputFieldsToUpdate = WidgetComponentHelpers.getAllComponentsOnState(this.widget.states[i]).filter(
        (c) =>
          c.id !== originalField.id &&
          (c.type === WidgetComponentType.InputText ||
            c.type === WidgetComponentType.Date ||
            c.type === WidgetComponentType.Dropdown ||
            c.type === WidgetComponentType.InternationalPhone),
      );
      if (inputFieldsToUpdate.length > 0) {
        inputFieldsToUpdate.forEach((field) => {
          let inputField;
          if (originalField?.label?.text && field?.label?.text) {
            inputField = WidgetComponentHelpers.updateLabelStylingFromExisting(field, originalField);
          } else {
            inputField = field;
          }
          if (diiferencesInStyle) {
            WidgetComponentHelpers.updateComponentsSingleStyleFromExisting(
              inputField,
              originalField,
              this.languageService,
              widget,
              diiferencesInStyle,
            );
          } else {
            WidgetComponentHelpers.updateComponentsStylingFromExisting(
              inputField,
              originalField,
              this.languageService,
              widget,
              null,
            );
          }
          requests$.push(this.widgetService.updateWidgetComponent(this.widget.id, inputField));
        });
      }
      this.generateRequestForFormComponents(originalField, inputFieldsToUpdate, this.widget.states[i], requests$);
    }
    return requests$.length > 0 ? forkJoin(requests$) : of([]);
  }

  public saveCurrentlyEditedComponent(): Observable<WidgetComponent> {
    if (this.currentlyEditedComponent) {
      return this.persistEditedComponent(this.currentlyEditedComponent.component);
    }
  }

  private markWidgetAsEdited(): void {
    this.widgetPreviewService.addEditedWidget(this.widgetState.widgetId, this.widgetState.previewImageUrl);
  }

  public persistEditedComponent(
    component: WidgetComponent,
    emptyCurrentAfterUpdate = true,
    regularStateId?: number,
    changingComponent?: boolean,
    automationId?: string,
  ): Observable<WidgetComponent> {
    emptyCurrentAfterUpdate = false;
    const clampedSpinComp = WidgetComponentHelpers.clampSpinTheWheelSize(component);
    if (clampedSpinComp && clampedSpinComp !== component) {
      this.persistEditedComponent(clampedSpinComp, false).subscribe();
    }
    if (WidgetComponentHelpers.isWidgetState(component)) {
      this.logger.debug('Should persist state changes');
      let oldComponent: any;
      // A snapshot is a baseline only for the object it was taken from; the flyouts persist their own copy.
      if (component === this.currentlyEditedComponent?.component && this.currentlyEditedComponent?.originalComponent) {
        oldComponent = JSON.parse(JSON.stringify(this.currentlyEditedComponent.originalComponent));
      }
      if (!WidgetComponentHelpers.componentHasChanges(oldComponent, component)) {
        if (!this.widgetTransition) {
          this.logger.debug('State has no changes');
          return of(component);
        }
        const isWidgetTransition = !!this.widgetTransition.widget;
        this.widgetTransitionBS.next(null);
        if (isWidgetTransition) {
          // Transition is saved on the widget, saving the unchanged state could land its response on another state
          this.updateInProgress = WidgetUpdateProgress.UpToDate;
          return of(component);
        }
      }
      this.updateInProgress = WidgetUpdateProgress.Updating;
      if (this.currentlyEditedComponent && Object.keys(this.currentlyEditedComponent).length) {
        this.isChanges = WidgetComponentHelpers.removeExtraPropertyForComparisonState(
          JSON.parse(JSON.stringify(this.currentlyEditedComponent.originalComponent)),
          JSON.parse(JSON.stringify(component)),
          this.isChanges,
          this.widgetService.hasUnpublishedChangesBS.getValue(),
        );
      }
      component.automationId = this.widgetState.automationId || component.automationId;
      const persist$ = this.widgetService.updateWidgetState(this.widget.id, component).pipe(share());
      persist$.subscribe(
        () => {
          this.markWidgetAsEdited();
          this.widgetService.hasUnpublishedChangesBS.next(true);
          this.savedStateBS.next(component);
          // If everything went well send UNDEFINED as currently edited component
          if (emptyCurrentAfterUpdate) {
            this.currentlyEditedComponentBS.next(undefined);
          } else if (!changingComponent) {
            this.refreshCurrentlyEditedComponent(component);
          }
          this.updateInProgress = WidgetUpdateProgress.UpToDate;
          // Update containers in state as in editor only visible containers are affected
          const stateIndex = this.widget.states.findIndex((state) => state.id === component.id);
          if (stateIndex !== -1) {
            this.widget.states[stateIndex] = component;
          }
          // We can't just rewrite whole widget state because it will cause blinking in editor as containers are inside the state
          // only for form setting - when form is set from different state
          if (regularStateId && this.widgetState.id !== regularStateId) {
            this.refreshStateInWidget(component);
          } else {
            WidgetStatesService.updateStateRenderableProperties(this.widgetState, component);
            this.refreshStateInWidget(this.widgetState);
          }
          this.actionOnComponentBS.next({ action: ActionOnComponent.Edit, component });
          // A state is named after its content, so editing that content renames its linked automation too.
          this.refreshLinkedAutomationSource(component);
        },
        () => {
          this.updateInProgress = WidgetUpdateProgress.Error;
          this.showWidgetUpdateSnackbar(component, true);
        },
      );
      return persist$;
    } else if (WidgetComponentHelpers.isWidgetContainer(component)) {
      this.logger.debug('Should persist container changes');
      const oldComponent = JSON.parse(JSON.stringify(this.currentlyEditedComponent.originalComponent));
      if (!WidgetComponentHelpers.componentHasChanges(oldComponent, component)) {
        this.logger.debug('Container has no changes');
        if (emptyCurrentAfterUpdate) {
          this.currentlyEditedComponentBS.next(undefined);
        }
        return of(component);
      }
      const state = this.currentInnerCanvasState ?? this.widgetState;
      this.updateInProgress = WidgetUpdateProgress.Updating;
      this.isChanges = WidgetComponentHelpers.compareComponent(
        { ...this.currentlyEditedComponent.originalComponent },
        { ...this.currentlyEditedComponent.component },
        this.isChanges,
        this.widgetService.hasUnpublishedChangesBS.getValue(),
      );
      const isChanges = !this.isChanges ? this.widgetService.hasUnpublishedChangesBS.getValue() : this.isChanges;
      const persist$ = this.widgetService
        .updateWidgetContainer(
          this.widget.id,
          state.id,
          component,
          this.currentDeviceView,
          this.currentlyEditedComponent.originalComponent,
        )
        .pipe(share());
      persist$.subscribe(
        () => {
          this.markWidgetAsEdited();
          this.widgetService.hasUnpublishedChangesBS.next(isChanges);
          // If everything went well send UNDEFINED as currently edited component
          if (emptyCurrentAfterUpdate) {
            this.currentlyEditedComponentBS.next(undefined);
          } else if (!changingComponent) {
            this.refreshCurrentlyEditedComponent(component);
          }
          this.updateInProgress = WidgetUpdateProgress.UpToDate;
          // Update containers in state as in editor only visible containers are affected
          const containerIndex = state.containers.findIndex((cont) => cont.id === component.id);
          if (containerIndex !== -1) {
            // Preserve components — they may have changed while the save was in flight.
            const currentComponents = state.containers[containerIndex].components;
            state.containers[containerIndex] = component;
            if (currentComponents) {
              state.containers[containerIndex].components = currentComponents;
            }
          }
          this.refreshStateInWidget(state);
          this.resetVisibleItemIds();
        },
        () => {
          this.updateInProgress = WidgetUpdateProgress.Error;
          this.showWidgetUpdateSnackbar(component, true);
        },
      );
      return persist$;
    } else {
      this.logger.debug('Should persist component changes');
      if (!component.containerId) {
        throw Error('ContainerId was missing on component');
      }
      let oldComponent: any;
      if (this.currentlyEditedComponent.originalComponent) {
        oldComponent = JSON.parse(JSON.stringify(this.currentlyEditedComponent.originalComponent));
      }
      if (!WidgetComponentHelpers.componentHasChanges(oldComponent, component)) {
        this.logger.debug('Component has no changes');
        if (emptyCurrentAfterUpdate) {
          this.currentlyEditedComponentBS.next(undefined);
        }
        return of(component);
      }
      this.updateInProgress = WidgetUpdateProgress.Updating;
      const widgetState = regularStateId ? regularStateId : this.widgetState.id;
      const persist$ = this.widgetService.updateWidgetComponent(this.widget.id, component).pipe(share());
      persist$.subscribe(
        (responseComponent: WidgetComponent) => {
          this.markWidgetAsEdited();
          this.isChanges = false;
          if (
            this.currentlyEditedComponent &&
            this.currentlyEditedComponent.component &&
            Object.keys(this.currentlyEditedComponent).length &&
            this.currentlyEditedComponent.component.id === responseComponent.id
          ) {
            this.isChanges = WidgetComponentHelpers.compareComponent(
              { ...this.currentlyEditedComponent.originalComponent },
              { ...this.currentlyEditedComponent.component },
              this.isChanges,
              this.widgetService.hasUnpublishedChangesBS.getValue(),
            );
          } else {
            this.isChanges = WidgetComponentHelpers.compareComponent(
              { ...this.originalComponent },
              { ...component },
              this.isChanges,
              this.widgetService.hasUnpublishedChangesBS.getValue(),
            );
          }
          const isChanges = !this.isChanges ? this.widgetService.hasUnpublishedChangesBS.getValue() : this.isChanges;
          this.widgetService.hasUnpublishedChangesBS.next(isChanges);
          // If everything went well send UNDEFINED as currently edited component
          if (emptyCurrentAfterUpdate) {
            this.currentlyEditedComponentBS.next(undefined);
          } else if (!changingComponent) {
            this.refreshCurrentlyEditedComponent(component);
          }
          this.refreshComponentInContainer(component);
          this.refreshStateInWidget(this.widgetState, automationId);
          this.actionOnComponentBS.next({ action: ActionOnComponent.Edit, component });
          this.updateInProgress = WidgetUpdateProgress.UpToDate;
          this.persistLastButtonIfMarked(component);
          this.refreshLinkedAutomationSource(component);
          this.promptUnlinkedAutomationDeletion(oldComponent, component);

          // Create or update session condition required for this widget
          this.updateIdentifiedEmailMarketingCondition(responseComponent);
          if (responseComponent.subType === WidgetComponentSubType.ButtonListSubscriptionCheckbox) {
            this.modifyAutomationInRealTime(this.currentContextService.currentContext.account, responseComponent);
          }
          if (
            WidgetAutomationHelpers.getSocialSecurityInput(responseComponent) &&
            !responseComponent.name.includes('socialSecurityNumber')
          ) {
            if (
              (oldComponent &&
                responseComponent.validationProperties.type !== oldComponent?.validationProperties?.type) ||
              !oldComponent
            ) {
              this.updateFieldNameToSocialSecurityNumber(
                { ...responseComponent, containerId: component.containerId },
                this.widget.id,
                widgetState,
              );
            }
          }
        },
        () => {
          this.updateInProgress = WidgetUpdateProgress.Error;
          this.showWidgetUpdateSnackbar(component, true);
        },
      );
      return persist$;
    }
  }

  // The button text may have changed since the automation was created, so the linked automation's display
  // metadata is refreshed on save. Fire-and-forget: a failure (e.g. deleted automation) must not affect saving.
  private refreshLinkedAutomationSource(component: WidgetComponent | WidgetState): void {
    if (!this.widget?.id) {
      return;
    }
    const linkedItems: Array<WidgetComponent | WidgetState> = [
      component,
      ...((component as WidgetComponent).children ?? []),
    ];
    // A state is named after the content inside it, so editing that content renames the state's automation too.
    const state = this.currentInnerCanvasState ?? this.widgetState;
    if (state?.actionAutomationId && !WidgetComponentHelpers.isWidgetState(component)) {
      linkedItems.push(state);
    }
    const linkedComponents = linkedItems.filter((item) => !!item?.actionAutomationId && !!item.id);
    linkedComponents.forEach((item) => {
      const sourceType = WidgetAutomationHelpers.toAutomationSourceType(
        item.type,
        WidgetComponentHelpers.isWidgetState(item),
      );
      const sourceLabel = WidgetAutomationHelpers.getComponentSourceLabel(item, this.languageService);
      const signature = `${sourceType}|${sourceLabel ?? ''}`;
      if (this.sentAutomationSourceByAutomationId.get(item.actionAutomationId) === signature) {
        return;
      }
      this.sentAutomationSourceByAutomationId.set(item.actionAutomationId, signature);
      this.widgetAutomationsService.patchAutomationSource(item.actionAutomationId, sourceType, sourceLabel).subscribe({
        error: (error) => {
          // Allow a retry on the next save.
          this.sentAutomationSourceByAutomationId.delete(item.actionAutomationId);
          this.logger.debug('Could not refresh automation source: ' + JSON.stringify(error));
        },
      });
    });
  }

  // Unchecking "trigger an automation" leaves the automation live but unreferenced, so offer to delete it.
  // Runs from the save's success handler: the component is already persisted, so the answer can't affect it.
  private promptUnlinkedAutomationDeletion(oldComponent: WidgetComponent, component: WidgetComponent): void {
    // A coupon component keeps a second reference, so both have to be compared or the one for
    // distribution is orphaned without ever being offered.
    const unlinked = ['actionAutomationId', 'impressionAutomationId'].flatMap((field) => {
      const previousByComponentId = new Map<number, string>(
        [oldComponent, ...(oldComponent?.children ?? [])]
          .filter((item) => !!item?.id && !!item[field])
          .map((item): [number, string] => [item.id, item[field]]),
      );
      if (!previousByComponentId.size) {
        return [];
      }
      return [component, ...(component.children ?? [])]
        .filter((item) => !!item?.id && !item[field])
        .map((item) => previousByComponentId.get(item.id))
        .filter((automationId) => !!automationId);
    });
    this.confirmUnlinkedAutomationDeletion(unlinked);
  }

  // One dialog for however many were unlinked - unticking clears both of a coupon's references at once.
  // Any that are already gone, deleted from the automations view, are dropped first so the offer matches
  // what is actually there.
  private confirmUnlinkedAutomationDeletion(automationIds: string[]): void {
    if (!automationIds.length) {
      return;
    }
    forkJoin(
      automationIds.map((automationId) =>
        this.widgetAutomationsService.getAutomationById(automationId).pipe(
          map((automation) => (automation ? automationId : null)),
          catchError(() => {
            this.logger.debug('Unlinked automation no longer exists: ' + automationId);
            return of(null);
          }),
        ),
      ),
    ).subscribe((results) => {
      const existing = results.filter((automationId) => !!automationId);
      if (existing.length) {
        this.openUnlinkedAutomationDeletionDialog(existing);
      }
    });
  }

  private openUnlinkedAutomationDeletionDialog(automationIds: string[]): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        maxWidth: '600px',
        minWidth: '300px',
        panelClass: 'confirm-dialog-panel',
        data: {
          title: 'generic.confirmDeleteDialog.title',
          text:
            automationIds.length > 1
              ? 'campaign.widget.sidebar.edit.button-action.delete-automations-text'
              : 'campaign.widget.sidebar.edit.button-action.delete-automation-text',
          proceedButtonText: 'generic.delete',
          cancelButtonText: 'campaign.widget.sidebar.edit.button-action.delete-automation-keep',
        },
      })
      .afterClosed()
      .subscribe((shouldDelete) => {
        if (!shouldDelete) {
          return;
        }
        forkJoin(automationIds.map((automationId) => this.automationService.deleteAutomation(automationId))).subscribe({
          next: () => {
            // Drop the label-PATCH signature so a later automation on the same id isn't suppressed.
            automationIds.forEach((automationId) => this.sentAutomationSourceByAutomationId.delete(automationId));
            this.snackbarService.success('campaign.widget.sidebar.edit.button-action.delete-automation-success');
          },
          error: () => this.snackbarService.error('generic.error'),
        });
      });
  }

  public updateIdentifiedEmailMarketingCondition(responseComponent): void {
    let sourceIntegration: string;
    if (responseComponent.subType === WidgetComponentSubType.ButtonListSubscriptionCheckbox) {
      sourceIntegration = responseComponent.subscriptionCheckboxProperties?.sourceIntegration;
    } else {
      sourceIntegration = WidgetComponentHelpers.getAllComponentsOfTypes(
        null,
        WidgetComponentHelpers.getAllComponentsOnWidget(this.widget as Widget),
        [WidgetComponentSubType.ButtonListSubscriptionCheckbox],
      )[0]?.subscriptionCheckboxProperties?.sourceIntegration;
    }
    const existingIdentifiedSubscriberCondition = this.widget.conditions?.find(
      (x) => x.type === SessionConditionType.IdentifiedEmailMarketing,
    );
    const isEmailInputExist = WidgetComponentHelpers.isExistComponentInWidget(
      this.widget,
      WidgetComponentSubType.InputTextEmail,
    );
    const isEmailInputResponseComponent =
      responseComponent.subType === WidgetComponentSubType.InputTextEmail ||
      !!(
        responseComponent.children &&
        responseComponent.children.find((child) => child.subType === WidgetComponentSubType.InputTextEmail)
      );
    if (
      (responseComponent.subType === WidgetComponentSubType.ButtonListSubscriptionCheckbox &&
        sourceIntegration &&
        existingIdentifiedSubscriberCondition?.value !== sourceIntegration) ||
      (isEmailInputResponseComponent && existingIdentifiedSubscriberCondition)
    ) {
      if (existingIdentifiedSubscriberCondition) {
        existingIdentifiedSubscriberCondition.value = sourceIntegration;
        existingIdentifiedSubscriberCondition.operator = isEmailInputExist
          ? AudienceFilterOperator.NotEquals
          : AudienceFilterOperator.Equals;
      } else {
        this.widget.conditions.push({
          operator: isEmailInputExist ? AudienceFilterOperator.NotEquals : AudienceFilterOperator.Equals,
          show: true,
          type: SessionConditionType.IdentifiedEmailMarketing,
          value: sourceIntegration,
        });
      }
      this.widgetService
        .createOrUpdateWidgetConditions(this.widget, this.widget.sessionConditionGroupId, this.widget.conditions)
        .subscribe(
          (response) => {
            this.widget.sessionConditionGroupId = response.sessionConditionGroupId;
            this.widget.conditions = response.sessionConditions;
          },
          (error) => {
            this.snackbarService.error('audience.messages.widget-session-condition-failed');
            this.logger.error('Error that should break the page, could not update condition: ' + JSON.stringify(error));
          },
        );
    }
  }

  // OpenNativeApp device targeting: "mobile only" adds the OnlyMobile condition; "mobile and desktop" removes it.
  public addOnlyMobileCondition(): void {
    this.widget.conditions = this.widget.conditions || [];
    const onlyMobile = this.widget.conditions.some(
      (c) => c.type === SessionConditionType.OnlyMobile && c.show !== false,
    );
    const anyDesktop = this.widget.conditions.some((c) => c.type === SessionConditionType.OnlyDesktop);
    if (onlyMobile && !anyDesktop) {
      return;
    }
    this.widget.conditions = this.widget.conditions.filter(
      (c) => c.type !== SessionConditionType.OnlyMobile && c.type !== SessionConditionType.OnlyDesktop,
    );
    this.widget.conditions.push({ show: true, type: SessionConditionType.OnlyMobile });
    this.persistOnlyMobileChange();
  }

  public removeOnlyMobileCondition(): void {
    if (!this.widget.conditions?.some((c) => c.type === SessionConditionType.OnlyMobile)) {
      return;
    }
    this.widget.conditions = this.widget.conditions.filter((c) => c.type !== SessionConditionType.OnlyMobile);
    this.persistOnlyMobileChange();
  }

  private persistOnlyMobileChange(): void {
    this.widgetService
      .createOrUpdateWidgetConditions(this.widget, this.widget.sessionConditionGroupId, this.widget.conditions)
      .subscribe(
        (response) => {
          this.widget.sessionConditionGroupId = response.sessionConditionGroupId;
          this.widget.conditions = response.sessionConditions;
        },
        (error) => {
          this.snackbarService.error('audience.messages.widget-session-condition-failed');
          this.logger.error('Could not update OnlyMobile condition: ' + JSON.stringify(error));
        },
      );
  }

  private refreshComponentInContainer(component: WidgetComponent) {
    const componentWithSiblings = WidgetComponentsService.getComponentByIdWithSiblings(this.widgetState, component.id);
    if (componentWithSiblings) {
      const foundComponent = componentWithSiblings.component;
      const index = componentWithSiblings.siblings.findIndex((x) => x.id === foundComponent.id);
      WidgetComponentsService.refreshWidgetComponentProperties(componentWithSiblings.siblings, foundComponent, index);
    }
    this.resetVisibleItemIds();
  }

  public refreshStateInWidget(widgetState: WidgetState, automationId?) {
    if (this.widget && this.widget.states) {
      let stateFound = false;
      for (let i = 0; i < this.widget.states.length && !stateFound; i++) {
        if (this.widget.states[i].id === widgetState.id) {
          if (automationId) widgetState.automationId = automationId;
          Object.assign(this.widget.states[i], widgetState);
          stateFound = true;
        }
      }
    }
  }

  private showWidgetUpdateSnackbar(component: any, isError = false) {
    let messageKey = undefined;
    if (WidgetComponentHelpers.isWidgetState(component)) {
      messageKey = isError ? 'campaign.widget.state-save-error' : 'campaign.widget.state-save-success';
    } else if (WidgetComponentHelpers.isWidgetContainer(component)) {
      messageKey = isError ? 'campaign.widget.container-save-error' : 'campaign.widget.container-save-success';
    } else {
      messageKey = isError ? 'campaign.widget.component-save-error' : 'campaign.widget.component-save-success';
    }

    if (isError === true) {
      this.snackbarService.error(messageKey, undefined, 5000, SnackBarPosition.BottomLeft);
    } else {
      this.updateInProgressBS.next({ messages: messageKey });
      this.snackbarService.success(messageKey, undefined, 5000, SnackBarPosition.BottomLeft);
    }
  }

  public updateWidgetStateOrder(stateId: number, newOrder: number, state?) {
    const persist$ = this.widgetService.moveWidgetState(this.widget.id, stateId, newOrder).pipe(share());

    persist$.subscribe(() => {
      this.widgetService.hasUnpublishedChangesBS.next(true);
      const previousPosition = this.widget.states.findIndex((state) => state.id === stateId);
      if (previousPosition !== -1) {
        const positionChanger = newOrder < previousPosition ? -0.1 : 0.1;
        const states = this.widget.states;
        states.find((state) => state.id === stateId).order = newOrder + positionChanger;
        // Finally loop through all containers and update components orders based on index
        states.sort((a, b) => a.order - b.order);
        states.forEach((state, index) => {
          state.order = index;
        });
        this.widget.states = states;
      } else if (state) {
        state.order = newOrder;
      }
    });
    return persist$;
  }

  public updateChildComponentsOrder(componentToReorder: WidgetComponent, previousPosition: number) {
    const positionChanger = componentToReorder.order < previousPosition ? -0.1 : 0.1;
    const parent = WidgetComponentsService.getParentComponent(this.widgetState, componentToReorder.parentId);

    parent.children.find((component) => component.id === componentToReorder.id).order =
      componentToReorder.order + positionChanger;

    // Finally update components orders based on index
    parent.children.sort((a, b) => a.order - b.order);
    parent.children.forEach((component, index) => {
      component.order = index;
    });

    this.refreshStateInWidget(this.widgetState);
    this.resetVisibleItemIds();
    this.updateCurrentlyEditedComponentBS(componentToReorder, EditedComponentPart.Style, undefined);
  }

  public updateContainersOrder(containerToReorder: WidgetComponent, previousPosition: number) {
    const positionChanger = containerToReorder.order < previousPosition ? -0.1 : 0.1;
    this.widgetState.containers.find((component) => component.id === containerToReorder.id).order =
      containerToReorder.order + positionChanger;
    this.widgetState.containers.sort((a, b) => a.order - b.order);
    this.widgetState.containers.forEach((component, index) => (component.order = index));

    this.refreshStateInWidget(this.widgetState);
    this.resetVisibleItemIds();
    this.updateCurrentlyEditedComponentBS(containerToReorder, EditedComponentPart.Style, undefined);
    // Reaches sibling containers even when nothing is selected for editing (e.g. sidebar drag & drop)
    this.forceRefreshOnComponent(containerToReorder);
  }

  public updateComponentsOrder(
    componentToReorder: WidgetComponent,
    previousContainerId: number,
    previousPosition: number,
    insideSidebar?,
  ) {
    const positionChanger =
      componentToReorder.order < previousPosition || componentToReorder.containerId !== previousContainerId
        ? -0.1
        : 0.1;

    // If container was changed remove component from previous one and add on the new one
    if (componentToReorder.containerId !== previousContainerId) {
      if (this.currentlyEditedComponent?.component?.id === componentToReorder.id) {
        this.currentlyEditedComponent.component.containerId = componentToReorder.containerId;
      }

      // Determine which states contain the previous and new containers
      const fromState = [this.widgetState, this.currentInnerCanvasState].find((s) =>
        s?.containers?.some((c) => c.id === previousContainerId),
      );
      const toState = [this.widgetState, this.currentInnerCanvasState].find((s) =>
        s?.containers?.some((c) => c.id === componentToReorder.containerId),
      );

      // Remove from previous container
      if (fromState) {
        const fromContainer = fromState.containers.find((c) => c.id === previousContainerId);
        if (fromContainer) {
          const indexInPreviousContainer = fromContainer.components.findIndex(
            (comp) => comp.id === componentToReorder.id,
          );
          if (indexInPreviousContainer !== -1) {
            fromContainer.components.splice(indexInPreviousContainer, 1);
          }
        }
      }

      if (componentToReorder.children && componentToReorder.children.length > 0) {
        componentToReorder.children.forEach((child) => (child.containerId = componentToReorder.containerId));
      }

      // Add to new container
      if (toState) {
        const toContainer = toState.containers.find((cont) => cont.id === componentToReorder.containerId);
        if (toContainer) {
          if (!toContainer.components) {
            toContainer.components = [];
          }
          const newIndexOfMovedComponent = toContainer.components.findIndex(
            (comp) => comp.id === componentToReorder.id,
          );
          if (newIndexOfMovedComponent !== -1) {
            toContainer.components[newIndexOfMovedComponent].order = componentToReorder.order + positionChanger;
            this.updateCurrentlyEditedComponentBS(componentToReorder, EditedComponentPart.Style, undefined);
          } else {
            // Add to new container
            this.logger.warn('Component does not exist in the container where it just got dropped, should not happen');
            componentToReorder.order += positionChanger;
            toContainer.components.push(componentToReorder);
          }
        }
      }
    } else {
      // If there was no change of container just set new order on component
      if (componentToReorder && this.currentlyEditedComponent?.component?.id === componentToReorder.id) {
        this.currentlyEditedComponent.component = {
          ...this.currentlyEditedComponent.component,
          order: componentToReorder.order,
        };
      }

      // Find the state containing the component and update its order
      const state = [this.widgetState, this.currentInnerCanvasState].find((s) =>
        s?.containers?.some((c) => c.id === componentToReorder.containerId),
      );

      if (state) {
        const container = state.containers.find((container) => container.id === componentToReorder.containerId);
        if (container) {
          const component = container.components.find((component) => component.id === componentToReorder.id);
          if (component) {
            component.order = componentToReorder.order + positionChanger;
          }
        }
      }
    }

    // Finally loop through all containers in both states and update components orders based on index
    [this.widgetState, this.currentInnerCanvasState].forEach((state) => {
      if (state?.containers) {
        state.containers.forEach((container) => {
          if (insideSidebar) {
            container.components.sort((a, b) => a.order - b.order);
          }
          container.components.forEach((component, index) => {
            component.order = index;
            if (component.id === componentToReorder.id) {
              componentToReorder.order = index;
            }
          });
        });
      }
    });

    this.forceRefreshOnComponent(componentToReorder);
    this.refreshStateInWidget(this.widgetState);
    this.resetVisibleItemIds();
  }

  public forceRefreshOnComponent(component: WidgetComponent) {
    this.refreshComponentSubject.next(component);
  }

  private getVisibleItemIdsFromState(state: WidgetState): { containers: number[]; components: number[] } {
    let visibleContainerIds: number[] = [];
    let visibleComponentIds: number[] = [];

    if (state && state.containers) {
      let visibleContainers = JSON.parse(JSON.stringify(state.containers)) as WidgetContainer[];
      visibleContainers = visibleContainers.filter(
        (container: WidgetContainer) =>
          (container.hideOnMobile !== true && this.currentDeviceView === WidgetDeviceView.Mobile) ||
          (container.hideOnDesktop !== true && this.currentDeviceView === WidgetDeviceView.Desktop),
      );

      if (visibleContainers.length === 1) {
        visibleContainers[0].percentWidth = 100;
      }

      // Process visible containers
      visibleContainers.forEach((container) => {
        container.components.forEach((component) => {
          if (component.children?.length) {
            const children = JSON.parse(JSON.stringify(component.children));
            const visibleChildren = WidgetComponentHelpers.filterVisibleComponents(this.currentDeviceView, children);
            visibleChildren?.forEach((child) => visibleComponentIds.push(Number(child.id)));
          }

          const comp = JSON.parse(JSON.stringify(component));
          const visibleComponents = WidgetComponentHelpers.filterVisibleComponents(this.currentDeviceView, [comp]);
          visibleComponents?.forEach((vc) => visibleComponentIds.push(Number(vc.id)));
        });
      });

      // Floating container components
      if (state.floatingContainer?.components) {
        const visibleFloatingComponents = WidgetComponentHelpers.filterVisibleComponents(
          this.currentDeviceView,
          state.floatingContainer.components,
        );
        visibleComponentIds = visibleComponentIds.concat(visibleFloatingComponents.map((comp) => Number(comp.id)));
      }

      visibleContainerIds = visibleContainers.map((cont) => Number(cont.id));
    }

    return { containers: visibleContainerIds, components: visibleComponentIds };
  }

  public resetVisibleItemIds() {
    const mainIds = this.getVisibleItemIdsFromState(this.widgetState);

    // Add ids from innerState
    if (this.widgetState?.isPrimaryCanvasState) {
      const secondIds = this.getVisibleItemIdsFromState(this.currentInnerCanvasState);
      const mergedContainers = [...new Set([...mainIds.containers, ...secondIds.containers])];
      const mergedComponents = [...new Set([...mainIds.components, ...secondIds.components])];

      this.visibleContainerIdsBS.next(mergedContainers);
      this.visibleComponentIdsBS.next(mergedComponents);
    } else {
      this.visibleContainerIdsBS.next(mainIds.containers);
      this.visibleComponentIdsBS.next(mainIds.components);
    }
  }

  public static getComponentsParentType(
    componentId: number,
    containers: any[],
  ): { type: WidgetComponentType; subType: WidgetComponentSubType } | undefined {
    if (!componentId || !containers) return undefined;
    for (const container of containers) {
      for (const comp of container.components) {
        if (comp.id === componentId) return undefined;
        if (comp.children) {
          for (const child of comp.children) {
            if (child.id === componentId) {
              return { type: comp.type, subType: comp.subType };
            }
          }
        }
      }
    }
    return undefined;
  }

  public isComponentChild(componentId: number) {
    if (!componentId || !this.widgetState || !this.widgetState.containers) {
      return false;
    }
    return WidgetComponentsService.getComponentById(this.widgetState, componentId)?.parentId !== undefined;
  }

  public modifyAutomationInRealTime(currentAccount, responseComponent) {
    const subscriptionCheckboxComponents = WidgetComponentHelpers.getAllComponentsOfTypes(
      null,
      WidgetComponentHelpers.getAllComponentsOnWidget(this.widget as Widget),
      [WidgetComponentSubType.ButtonListSubscriptionCheckbox],
    );
    const dataSource =
      subscriptionCheckboxComponents[0]?.subscriptionCheckboxProperties?.sourceIntegration ||
      responseComponent?.subscriptionCheckboxProperties?.sourceIntegration;
    const isIdentifierMatch = this.isIdentifierMatch(currentAccount).isIdentifierMatch;
    if (!dataSource) return;
    const automationId = this.widget.states.map((state) => state.automationId).filter((id) => id)?.[0];
    if (automationId) {
      this.updateAutomationWithRealTime(automationId, subscriptionCheckboxComponents[0], isIdentifierMatch, dataSource);
    } else {
      this.widgetService.getWidgetById(this.widget.id).subscribe((widget) => {
        const state = widget.states.find((state) => state.automationId);
        if (state?.automationId) {
          this.updateAutomationWithRealTime(
            state?.automationId,
            subscriptionCheckboxComponents[0],
            isIdentifierMatch,
            dataSource,
          );
        } else {
          this.createAutomationWithRealTime(dataSource, currentAccount, this.campaign, isIdentifierMatch);
        }
      });
    }
  }

  public isIdentifierMatch(currentAccount) {
    const inputComponents = WidgetComponentHelpers.getAllComponentsOfTypes(
      null,
      WidgetComponentHelpers.getAllComponentsOnWidget(this.widget as any),
      [
        WidgetComponentSubType.InputTextPhone,
        WidgetComponentSubType.InternationalPhoneRegular,
        WidgetComponentSubType.InputTextEmail,
      ],
    );
    let missedInputType: string;
    const isIdentifierMatch = this.widgetAutomationsDataService
      .combinationInputsForDifferentAccountIdentifier(currentAccount.identifier)
      .some((el) => {
        if (inputComponents?.length && inputComponents.map((comp) => comp.subType).includes(el)) {
          return true;
        } else {
          missedInputType = el;
        }
      });
    return { isIdentifierMatch, missedInputType };
  }

  public getMissedInputType(account) {
    const dataIdentifier = this.isIdentifierMatch(account);
    const missedInputType = dataIdentifier.missedInputType;
    const isIdentifierMatch = dataIdentifier.isIdentifierMatch;
    if (isIdentifierMatch) return;
    if (
      missedInputType === WidgetComponentSubType.InternationalPhoneRegular ||
      missedInputType === WidgetComponentSubType.InputTextPhone
    ) {
      return 'automations.phone-field-is-required';
    } else {
      return 'automations.email-field-is-required';
    }
  }

  public updateAutomationWithRealTime(
    automationId,
    subscriptionCheckboxComponent,
    isIdentifierMatch,
    nameAction,
  ): void {
    this.widgetAutomationsService.getAutomationById(automationId).subscribe((automation) => {
      const actionsRealTime = automation?.actions.filter((action) =>
        ActionRealTimeScl.includes(action.$type as ACTIONS_TYPES_REAL_TIME),
      );
      const actionsWithRealTime = automation?.actions.filter((action) =>
        ActionWithRealTime.includes(action.$type as ACTIONS_TYPES),
      );
      const actionWithRealTimeScl = actionsWithRealTime?.find((action) => action.$type.includes(nameAction));
      const actionRealTimeScl = actionsRealTime?.find((action) => action.$type.includes(nameAction));
      if (automation?.actions?.length && subscriptionCheckboxComponent) {
        automation.actions.forEach((action) => {
          if (isIdentifierMatch) {
            if (actionWithRealTimeScl && ActionWithRealTime.includes(action.$type as ACTIONS_TYPES)) {
              action.isRunningInRealTime = true;
            } else {
              if (
                !automation.actions.find(
                  (act) => act.$type === ActionWithRealTime.find((action) => action.includes(nameAction)),
                )
              ) {
                automation?.actions.push({
                  $type: ActionWithRealTime.find((action) => action.includes(nameAction)),
                  isRunningInRealTime: true,
                } as AutomationAction);
              }
            }
            if (actionRealTimeScl && action.$type === actionRealTimeScl.$type) {
              action.isRunningInRealTime = false;
            }
          } else {
            if (actionRealTimeScl && ActionRealTimeScl.includes(action.$type as ACTIONS_TYPES_REAL_TIME)) {
              action.isRunningInRealTime = true;
            } else if (!actionRealTimeScl) {
              automation.actions = automation?.actions.filter(
                (action) => !ActionRealTimeScl.includes(action.$type as ACTIONS_TYPES_REAL_TIME),
              );
              automation?.actions.push({
                $type: ActionRealTimeScl.find((action) => action.includes(nameAction)),
                isRunningInRealTime: true,
              } as AutomationAction);
            }
            if (actionWithRealTimeScl && ActionWithRealTime.includes(action.$type as ACTIONS_TYPES)) {
              automation.actions = automation?.actions.filter((action) => {
                return !(
                  ActionWithRealTime.includes(action.$type as ACTIONS_TYPES) && action.$type.includes(nameAction)
                );
              });
            }
          }
        });
      } else if (!automation?.actions.length && subscriptionCheckboxComponent) {
        automation?.actions.push({
          $type: isIdentifierMatch
            ? ActionWithRealTime.find((action) => action.includes(nameAction))
            : ActionRealTimeScl.find((action) => action.includes(nameAction)),
          isRunningInRealTime: true,
        } as AutomationAction);
      }
      if (!subscriptionCheckboxComponent) {
        if (actionWithRealTimeScl) {
          automation.actions = automation?.actions.filter((action) => action.$type !== actionWithRealTimeScl.$type);
        }
        if (actionRealTimeScl) {
          automation.actions = automation?.actions.filter((action) => action.$type !== actionRealTimeScl.$type);
        }
      }
      this.widgetAutomationsService.updateAutomation(automation).subscribe((automation) => {
        this.setDefaultFormSettingsOnWidget(automation);
      });
      this.widgetService.hasUnpublishedChangesBS.next(true);
    });
  }

  public updateAutomationWithRealTimeRegistrationType(automationId, type, value) {
    this.widgetAutomationsService.getAutomationById(automationId).subscribe((automation) => {
      if (value) {
        automation?.actions.push({
          $type: ActionRealTimeRegistrationType.find((action) => action.includes(type)),
          isRunningInRealTime: true,
        } as AutomationAction);
      } else {
        automation.actions = automation.actions.filter((action) => !action.$type.includes(type));
      }
      this.widgetAutomationsService.updateAutomation(automation).subscribe();
    });
  }

  public createAutomationWithRealTimeRegistrationType(type, campaignName, currentAccount, widgetState: WidgetState) {
    let automation = WidgetAutomationHelpers.setNameOnBlankAutomation(
      campaignName,
      currentAccount,
      this.languageService,
      this.widget,
    );
    automation.actions = [];
    automation?.actions.push({
      $type: ActionRealTimeRegistrationType.find((action) => action.includes(type)),
      isRunningInRealTime: true,
    } as AutomationAction);
    this.widgetAutomationsService.createAutomation(automation).subscribe((automation) => {
      widgetState.automationId = automation.id;
      this.widgetAutomationsDataService.existingAutomation = automation;
      this.updateAutomationIdOnCurrentState(automation.id, widgetState);
    });
  }

  public createAutomationWithRealTime(nameAction, currentAccount, campaign, isIdentifierMatch): void {
    let automation = WidgetAutomationHelpers.setNameOnBlankAutomation(
      campaign.name,
      currentAccount,
      this.languageService,
      this.widget,
    );
    automation.actions = [];
    automation.actions.push({
      $type: isIdentifierMatch
        ? ActionWithRealTime.find((action) => action.includes(nameAction))
        : ActionRealTimeScl.find((action) => action.includes(nameAction)),
      isRunningInRealTime: true,
    } as AutomationAction);
    this.widgetAutomationsService.createAutomation(automation).subscribe((automation) => {
      this.widgetState.automationId = automation.id;
      this.updateAutomationIdOnCurrentState(automation.id, this.widgetState);
      this.setDefaultFormSettingsOnWidget(automation);
    });
  }

  public isComponentAllowRealTime(responseComponent): boolean {
    return (
      responseComponent.subType === WidgetComponentSubType.InputTextEmail ||
      responseComponent.subType === WidgetComponentSubType.InputTextPhone ||
      responseComponent.subType === WidgetComponentSubType.InternationalPhoneRegular ||
      responseComponent.subType === WidgetComponentSubType.ButtonListSubscriptionCheckbox
    );
  }

  public clearAlternativeStep() {
    const submitButton = WidgetComponentHelpers.getComponentFromStates(
      this.widget?.states,
      WidgetComponentSubType.ButtonSubmit,
    );
    const submitNavButton = WidgetComponentHelpers.getComponentFromStates(
      this.widget?.states,
      WidgetComponentSubType.StepNavigationRegular,
    );
    const submitComp = submitButton || submitNavButton?.lastButtonType === StepNavigationLastButtonType.Submit;
    if (submitComp?.targetComponent?.actionValue || submitComp?.targetComponent?.actionType) {
      submitComp.targetComponent.actionType = ButtonActionType.CloseWidget;
      submitComp.targetComponent.actionValue = '';
      const automationId = this.widget.states.find(
        (state) => state.id === submitComp.targetState && state.automationId,
      )?.automationId;
      this.persistEditedComponent(
        submitComp.targetComponent,
        false,
        submitComp.targetState,
        null,
        automationId,
      ).subscribe();
    }
    if (
      this.flyoutService.flyoutFormSettings?.alternativeStateUsed &&
      this.flyoutService.flyoutFormSettings?.componentId &&
      this.widget
    ) {
      const widgetState = this.widget.states.find(
        (state) => state.id === this.flyoutService.flyoutFormSettings?.componentId,
      );
      this.setEmtyAlternative(widgetState);
    } else if (this.widget) {
      this.widget.states.forEach((widgetState) => {
        const widgetStateUntyped = <any>widgetState;
        if (
          widgetStateUntyped.alternativeStepApp ||
          widgetStateUntyped.alternativeStepListId ||
          widgetStateUntyped.alternativeStepId ||
          widgetStateUntyped.subscriberCheckApp ||
          widgetStateUntyped.subscriberCheckListId ||
          widgetStateUntyped.subscriberCheckStateId
        ) {
          this.setEmtyAlternative(widgetStateUntyped);
        }
      });
    }
  }

  private setEmtyAlternative(widgetState): void {
    widgetState.alternativeStepApp = undefined;
    widgetState.alternativeStepListId = undefined;
    widgetState.alternativeStepId = undefined;
    widgetState.subscriberCheckApp = null;
    widgetState.subscriberCheckListId = null;
    widgetState.subscriberCheckStateId = null;
    this.persistEditedComponent(widgetState, false, widgetState.id).subscribe();
  }

  async setDefaultFormSettingsOnWidget(automation, isRegisterType?) {
    if (automation?.actions?.some((action) => action.isRunningInRealTime) || isRegisterType) {
      const realTimeHandler = {};
      if (this.widget && !this.widget.formSettings) {
        Object.values(FormSettingsHandlerTypes).forEach((prop) => {
          realTimeHandler[prop] = {};
          realTimeHandler[prop].defaultMessage = this.accountLanguageService.translate(
            'defaultRealTimeMessage.' + prop,
          );
          realTimeHandler[prop].message = realTimeHandler[prop].defaultMessage;
          realTimeHandler[prop].stepId = null;
          realTimeHandler[prop].$type = REAL_TIME_HANDLER_TYPE;
        });
        await this.widgetService
          .patchWidget(this.widget.id, '/formSettings', realTimeHandler)
          .subscribe(() => this.widgetBS.next({ ...this.widget, formSettings: realTimeHandler as FormSettings }));
        await this.clearAlternativeStep();
      }
      this.clearAlternativeStep();
    }
  }

  public setCurrentDeviceView(deviceView: WidgetDeviceView) {
    this.currentDeviceViewBS.next(deviceView);
    this.resetVisibleItemIds();
  }

  public get visibleContainerIds(): Array<number> {
    return this.visibleContainerIdsBS.getValue();
  }

  public get visibleComponentIds(): Array<number> {
    return this.visibleComponentIdsBS.getValue();
  }

  public get campaign(): OnsiteCampaign {
    return this.campaignBS.getValue();
  }

  public get widget(): Widget {
    return this.widgetBS.getValue();
  }

  public get widgetState(): WidgetState {
    return this.widgetStateBS.getValue();
  }

  public get currentlyEditedComponent(): EditModeComponent {
    return this.currentlyEditedComponentBS.getValue();
  }

  public get currentDeviceView(): WidgetDeviceView {
    return this.currentDeviceViewBS.getValue();
  }

  public get hoveredComponent(): WidgetComponent {
    return this.hoveredComponentBS.getValue();
  }

  public get isCurrentTabChanged(): boolean {
    return this.isCurrentTabChangedBS.getValue();
  }

  public set hoveredComponent(widgetComponent: WidgetComponent) {
    this.hoveredComponentBS.next(widgetComponent);
  }

  public get customFonts(): Array<WidgetFontDescription> {
    return this.customFontsBS.getValue();
  }

  public get accountVariables(): Array<AccountVariable> {
    return this.accountVariablesBS.getValue();
  }

  public get simpleWidgets(): Array<WidgetSimpleInfo> {
    return this.simpleWidgetsBS.getValue();
  }

  public get allTags(): Array<string> {
    return this.allTagsBS.getValue();
  }

  public get customProfileProperties(): Array<CustomProfileProperty> {
    return this.customProfilePropertiesBS.getValue();
  }

  public get widgetTransition() {
    return this.widgetTransitionBS.value;
  }

  public removeComponentFromClipboard(position: number) {
    const components = JSON.parse(sessionStorage.getItem('clipboard-components'));
    components.splice(position, 1);
    sessionStorage.setItem('clipboard-components', JSON.stringify(components));
    this.clipboardComponentsBS.next(this.getClipboardComponents());
  }

  public storeComponentToClipboard(component: WidgetComponent) {
    let components = JSON.parse(sessionStorage.getItem('clipboard-components'));
    // Need to reset parentData so we do not have that when pasting in other steps or campaigns.
    component.parentId = undefined;
    component.parentComponentSubType = undefined;
    component.parentBackground = undefined;
    component.parentComponentType = undefined;
    if (!components) {
      components = [];
    }

    components.unshift(JSON.stringify(component));
    sessionStorage.setItem('clipboard-components', JSON.stringify(components.slice(0, 4)));
    this.clipboardComponentsBS.next(this.getClipboardComponents());
    this.snackbarService.success('campaign.widget.copy-success');

    this.currentlyEditedComponentBS.next({
      component: undefined,
      originalComponent: undefined,
      sidebarSection: EditedComponentSidebarSection.AddAfterCopy,
      isCloseEditorComponent: true,
    } as EditModeComponent);

    this.showLeftSidebarBS.next({ sidebarState: SidebarState.New, shouldAnimateCopy: true });
  }

  public copyComponent(component: WidgetComponent, currentAccount: Account, openAddWindow = false) {
    const componentCopy = JSON.parse(JSON.stringify(component)) as WidgetComponent;
    componentCopy.order = component.order + 1;
    componentCopy.name = WidgetComponentHelpers.generateWidgetComponentTextFieldName(this.widget, component.subType);
    // The copy starts unlinked so it never references the source's automation; the create request carries the
    // source id instead, and the backend links its own copy of that automation to the new component.
    const sourceAutomationId = component.actionAutomationId;
    componentCopy.actionAutomationId = null;
    if (!componentCopy.parentId) {
      if (component.subType === WidgetComponentSubType.TextFloating) {
        componentCopy.positionX = component.positionX + 50;
      }

      this.addComponentToWidgetState(
        componentCopy,
        componentCopy.order,
        componentCopy.containerId,
        currentAccount,
        false,
        undefined,
        true,
        openAddWindow,
        true,
        true,
        false,
        sourceAutomationId,
      ).subscribe(
        (createdComponent: WidgetComponent) => {
          this.logger.debug('Component added with copy: ' + JSON.stringify(createdComponent));
          this.snackbarService.success('campaign.widget.dublicate-success');
        },
        () => {
          this.logger.error('Error while adding copied component');
          this.snackbarService.error('generic.error');
        },
      );
    } else {
      throw new Error('Child component should not be able to be added by copy');
    }
  }

  public updateWidgetComponent(component: WidgetComponent, widget?) {
    if (!this.widget) {
      this.widgetService.updateWidgetComponent(widget?.id, component).subscribe();
    } else {
      this.widgetService.updateWidgetComponent(this.widget.id, component).subscribe();
    }
  }

  public updateWidgetComponentObservable(component: WidgetComponent, widget) {
    if (!this.widget) {
      return this.widgetService.updateWidgetComponent(widget?.id, component);
    } else {
      return this.widgetService.updateWidgetComponent(this.widget.id, component);
    }
  }

  public shouldRefreshComponentInSidebarSection(
    editModeComponent: EditModeComponent,
    section: EditedComponentSidebarSection,
  ) {
    if (!editModeComponent || !section) {
      return true;
    }
    if (
      editModeComponent.latestUpdatedPart === EditedComponentPart.Style &&
      editModeComponent.sidebarSection === section &&
      !this.isCurrentTabChanged
    ) {
      return false;
    }
    return true;
  }

  public resetLeftSidebar() {
    this.showLeftSidebarBS.next({
      sidebarState: SidebarState.Layout,
      shouldAnimateCopy: false,
    });
  }

  public updateFieldNameToSocialSecurityNumber(component: WidgetComponent, widgetId, widgetState) {
    const isSocialNumberExist = WidgetComponentHelpers.getAllComponentsOfTypes(
      null,
      WidgetComponentHelpers.getAllComponentsOnWidget(this.widget as any),
      [WidgetComponentSubType.InputTextRegular],
    )?.some((comp) => comp.name === 'socialSecurityNumber');
    if (isSocialNumberExist) return;
    let automationId = this.widget.states.map((state) => state.automationId).filter((id) => id)?.[0];
    if (!automationId) {
      this.widgetService.getWidgetById(this.widget.id).subscribe((widget) => {
        automationId = widget.states.find((state) => state.automationId)?.automationId;
        if (!automationId) return;
        this.callConfirmDialogForSocialSecurityNumber(automationId, widgetId, widgetState, component);
      });
    } else {
      this.callConfirmDialogForSocialSecurityNumber(automationId, widgetId, widgetState, component);
    }
  }

  public callConfirmDialogForSocialSecurityNumber(automationId, widgetId, widgetState, component) {
    this.widgetAutomationsService.getAutomationById(automationId).subscribe((automation) => {
      if (!automation) return;
      const isRealTimeVoyado = automation.actions.some(
        (action) => action.$type === ACTIONS_TYPES.VoyadoAddEmailToList && action.isRunningInRealTime === true,
      );
      if (isRealTimeVoyado) {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
          maxWidth: '600px',
          minWidth: '300px',
          panelClass: 'confirm-dialog-panel',
          data: {
            title: 'confirmationDialog.confirmationTitle',
            text: 'confirmationDialog.setSocialNumberId',
          },
        });
        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            component.name = 'socialSecurityNumber';
            this.widgetService.updateWidgetComponent(widgetId, component).subscribe(
              (updatedComponent) => {
                this.forceRefreshOnComponent(updatedComponent);
                this.refreshComponentSubject.next(updatedComponent);
                this.confirmSocialSecurityNumberResult$.next(true);
              },
              () => {
                this.snackbarService.error('generic.error');
                this.confirmSocialSecurityNumberResult$.next(false);
              },
            );
          } else {
            this.confirmSocialSecurityNumberResult$.next(false);
          }
        });
      } else {
        this.confirmSocialSecurityNumberResult$.next(true);
      }
    });
  }

  public checkAndUpdateComponentBeforeSwitching(): Observable<boolean> {
    if (this.isCurrentComponentUpdated()) {
      return this.persistEditedComponent(this.currentlyEditedComponent.component, true).pipe(map(() => true));
    } else {
      return of(true);
    }
  }

  public reCalculateContainersWidthWhenDelete(component) {
    const containersCount = this.widgetState.containers.length;
    let percentsToSet = null;
    if (containersCount === 2) {
      percentsToSet = 100;
    } else if (
      containersCount === 3 &&
      this.widgetState.containers[0].percentWidth === 33 &&
      this.widgetState.containers[1].percentWidth === 33
    ) {
      percentsToSet = 50;
    } else {
      if (this.widgetState.containers[this.widgetState.containers.length - 1].id !== component.id) {
        this.widgetState.containers[this.widgetState.containers.length - 1].percentWidth += (
          component as unknown as WidgetContainer
        ).percentWidth;
      } else if (this.widgetState.containers.length > 1) {
        this.widgetState.containers[this.widgetState.containers.length - 2].percentWidth += (
          component as unknown as WidgetContainer
        ).percentWidth;
      }
    }
    this.widgetState.containers.forEach((container) => {
      if (component.id !== container.id) {
        container.percentWidth = percentsToSet ? percentsToSet : container.percentWidth;
      }
    });
  }

  public saveComponentUpdatesIfAny() {
    if (this.checkIfSaveable()) {
      if (this.currentlyEditedComponent && this.currentlyEditedComponent.component) {
        this.persistEditedComponent(this.currentlyEditedComponent.component).subscribe(
          (updatedComponent: WidgetComponent) => {
            this.logger.debug('Saved updated component before opening menu: ' + JSON.stringify(updatedComponent));
          },
        );
      }
    } else {
      this.discardInvalidEdits();
    }
  }

  public isNotOnPrimaryCanvasState(component: WidgetComponent) {
    if (component && this.currentInnerCanvasState) {
      const states = this.widget.states;
      const state = WidgetStatesService.getStateByComponentId(states, component.id);
      if (state?.isPrimaryCanvasState) {
        // Not editable here, GoToPrimaryCanvasComponent will be shown
        return true;
      }
    }
    return false;
  }

  private getClipboardComponents(): WidgetComponent[] {
    const components = JSON.parse(sessionStorage.getItem('clipboard-components')) ?? [];
    return components.map((c) => (typeof c === 'string' ? JSON.parse(c) : c));
  }

  public markLastButtonForPersist(lastButtonChild: WidgetComponent | undefined): void {
    this.lastButtonMarkedForPersist = lastButtonChild;
  }

  private persistLastButtonIfMarked(component: WidgetComponent): void {
    if (component.type !== WidgetComponentType.StepNavigation || !this.lastButtonMarkedForPersist) {
      return;
    }
    const lastButtonChild = this.lastButtonMarkedForPersist;
    this.lastButtonMarkedForPersist = undefined;
    this.widgetService
      .updateWidgetComponent(this.widget.id!, lastButtonChild)
      .subscribe(() => this.forceRefreshOnComponent(lastButtonChild));
  }
}
